import instructorLogo from '../school-brand.jpg';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingCalendar from './BookingCalendar';
import StudentProgressReport from './StudentProgressReport';
import InstructorHelpModal from './InstructorHelpModal';

// =================================================================
// 1. CONSTANTS, SCHEMAS & HOOKS
// =================================================================
import { DYNAMIC_VEHICLE_FRAMEWORKS, MASTER_READINESS_CHECKLIST, scoringGuide } from './constants';
import * as Styles from './styles';
import SettingsDrawer from './SettingsDrawer';
import { usePostcodeSearch } from './usePostcodeSearch';

function InstructorDash() {
    const navigate = useNavigate();

    // =================================================================
    // 2. STATE MANAGEMENT (All hooks declared first unconditionally)
    // =================================================================
    const [learners, setLearners] = useState([]);
    const [template, setTemplate] = useState([]); 
    const [instructorBookings, setInstructorBookings] = useState([]);
    const [historicalLogs, setHistoricalLogs] = useState([]);    
    const [allLogsCache, setAllLogsCache] = useState([]);

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [isSaving, setIsSaving] = useState(false); 
    const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
const [lessonBufferMinutes, setLessonBufferMinutes] = useState(0);
    const [modalActiveTab, setModalActiveTab] = useState('lesson');

    // --- NEW: Booking Management States ---
    const [slotSelectedLearnerId, setSlotSelectedLearnerId] = useState('');
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showManageModal, setShowManageModal] = useState(false);
    const [actionType, setActionType] = useState('view'); // 'view', 'move', 'message'
    const [newBookingDate, setNewBookingDate] = useState('');
    const [newBookingTime, setNewBookingTime] = useState('');
    const [messageText, setMessageText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const [instructorName, setInstructorName] = useState('');
    const [instructorDob, setInstructorDob] = useState('');
    const [instructorBadgeNumber, setInstructorBadgeNumber] = useState('');
    const [studentName, setStudentName] = useState('');
    const [studentEmail, setStudentEmail] = useState('');
    const [badgeExpiry, setBadgeExpiry] = useState('');
    const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
    const [logDuration, setLogDuration] = useState(60);   
    const [logProgressScore, setLogProgressScore] = useState(3);
    const [customLogNotes, setCustomLogNotes] = useState('');
    const [nextStepsPlan, setNextStepsPlan] = useState('');
    const [readinessChecklist, setReadinessChecklist] = useState([]);
    const [slotDurationHours, setSlotDurationHours] = useState(1);
    const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
    const [workStart, setWorkStart] = useState("08:00");
    const [workEnd, setWorkEnd] = useState("19:00");
    const [cancellationLockHours, setCancellationLockHours] = useState(24);
    const [instructorEmail, setInstructorEmail] = useState('');
    const [instructorPhone, setInstructorPhone] = useState('');
    const [instructorAddress, setInstructorAddress] = useState('');
    const [instructorPostcode, setInstructorPostcode] = useState('');
    const [travelRadius, setTravelRadius] = useState(5);
    const [trainingCategory, setTrainingCategory] = useState('Car (Manual)');
    const [vehicleCategories, setVehicleCategories] = useState(["Car (Manual)"]);
    const [hqPostcode, setHqPostcode] = useState('');
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [slotPickupType, setSlotPickupType] = useState('primary');
    const [slotCustomPickup, setSlotCustomPickup] = useState('');
    const {
        nearbyAreas,
        findNearbyAreas,
latitude: postcodeLatitude,
        longitude: postcodeLongitude
    } = usePostcodeSearch();

    const [newPassword, setNewPassword] = useState('');
    const [selection, setSelection] = useState(null); 
    const [isEditingProfile, setIsEditingProfile] = useState(false);

    // Safely retrieve session context
    const instructorId = localStorage.getItem('instructorId');
    const userRole = localStorage.getItem('userRole');
    const [slotIntent, setSlotIntent] = useState('book'); // 'book' or 'block'
    const [isMatrixOpen, setIsMatrixOpen] = useState(false);
const modalContentRef = React.useRef(null);

const [isHelpOpen, setIsHelpOpen] = useState(false);
React.useEffect(() => {
    if (showProgressModal) {
        // Use a tiny timeout to ensure the modal element has mounted to the DOM
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            if (modalContentRef.current) {
                modalContentRef.current.scrollTop = 0;
            }
        }, 10);
        return () => clearTimeout(timer);
    }
}, [showProgressModal, modalActiveTab, selectedStudent]);


const [summaryCues, setSummaryCues] = useState([
  "✨ Drove Well",
  "🛡️ Safety Great",
  "🎯 Good Car Control",
  "⚠️ Needs Observation",
  "💡 Progressing Well"
]);

const [nextStepsCues, setNextStepsCues] = useState([
  "🔄 Target Reversing",
  "🚦 Target Junctions",
  "🅿️ Bay Parking Focus",
  "🛣️ Dual Carriageway Practice",
  "🛑 Emergency Stop Refresher"
]);



// --- BROADCAST STATES ---
const [broadcastText, setBroadcastText] = useState('');
const [isBroadcasting, setIsBroadcasting] = useState(false);

const [showDirectMessageModal, setShowDirectMessageModal] = useState(false);
const [targetMessageStudent, setTargetMessageStudent] = useState(null);
const [directMessageText, setDirectMessageText] = useState('');

const [adminMessages, setAdminMessages] = useState([]);
const [adminReplyText, setAdminReplyText] = useState('');



// --- LIVE DISPATCH TICKER STATES ---
const [dispatchMessages, setDispatchMessages] = useState([]);
const [replyTexts, setReplyTexts] = useState({});
const [dismissedAdminIds, setDismissedAdminIds] = useState([]); // 👈 Tracks locally dismissed notices

const fetchDispatchMessages = useCallback(async () => {
    const currentInstructorId = localStorage.getItem('instructorId') || '1';
    
    try {
       const res = await fetch(`/api/messages/instructor/${currentInstructorId}`, { cache: 'no-store' });
        if (res.ok) {
            const data = await res.json();
            console.log("📦 [TICKER] Fetched dispatch messages:", data);
            
            // 👇 SPLIT & FILTER DATA HERE: Exclude locally dismissed admin notices
            if (Array.isArray(data)) {
                setDispatchMessages(data.filter(m => m.learner_id)); // Student dispatches
                setAdminMessages(data.filter(m => !m.learner_id && !dismissedAdminIds.includes(m.id))); // Admin notices
            } else {
                setDispatchMessages([]);
                setAdminMessages([]);
            }
        } else {
            console.warn("⚠️ [TICKER] Response not OK:", res.status);
        }
    } catch (err) {
        console.error("❌ [TICKER] Error fetching dispatch messages:", err);
    }
}, [dismissedAdminIds]);

useEffect(() => {
    fetchDispatchMessages();

    const interval = setInterval(fetchDispatchMessages, 5000);
    return () => clearInterval(interval);
}, [fetchDispatchMessages]);

const handleDeleteAdminMessage = async (messageId) => {
    // 1. Instantly remove it from the screen
    setAdminMessages(prev => prev.filter(m => m.id !== messageId));

    try {
        const res = await fetch(`/api/messages/${messageId}`, { method: 'DELETE' });
        if (!res.ok) {
            console.error("Failed to delete message on server");
        }
    } catch (err) {
        console.error("Error deleting message:", err);
    }
};


const handleSendDirectMessage = async () => {
    if (!directMessageText.trim() || !targetMessageStudent) return;

    try {
        const res = await fetch('/api/messages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                instructor_id: parseInt(instructorId, 10),
                learner_id: parseInt(targetMessageStudent.id, 10),
                sender_role: 'instructor',
                message_text: directMessageText.trim()
            })
        });

        if (res.ok) {
            alert(`✉️ Direct message successfully sent to ${targetMessageStudent.name}!`);
            setDirectMessageText('');
            setShowDirectMessageModal(false);
            setTargetMessageStudent(null);
        } else {
            alert("❌ Failed to send direct message.");
        }
    } catch (err) {
        console.error("Direct message error:", err);
        alert("❌ Server connection error.");
    }
};

const handleSendInstructorReply = async (learnerId) => {
    const text = replyTexts[learnerId];
    if (!text || !text.trim()) return;
    const currentInstructorId = localStorage.getItem('instructorId') || '1';

    try {
const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        instructor_id: parseInt(currentInstructorId, 10),
        learner_id: parseInt(learnerId, 10),
        sender_role: 'instructor',
        message_text: text.trim()
    })
});

        if (res.ok) {
            setReplyTexts(prev => ({ ...prev, [learnerId]: '' }));
            fetchDispatchMessages();
        }
    } catch (err) {
        console.error("Error sending instructor reply:", err);
    }
};

    // =================================================================
    // 3. AUTH GUARD & SECURITY EFFECT
    // =================================================================
    useEffect(() => {
        if (!instructorId || instructorId === 'undefined' || instructorId === 'null' || userRole !== 'instructor') {
            navigate('/');
        }
    }, [instructorId, userRole, navigate]);

const timeLabels = useMemo(() => [
        "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", 
        "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", 
        "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", 
        "17:00", "17:30", "18:00"
    ], []);
      
    // =================================================================
    // 4. DATA FETCHING 
    // =================================================================

    const fetchData = useCallback(async () => {
        if (!instructorId) return;
        
        try {
            const [lRes, iRes, bRes, logsRes] = await Promise.all([
                fetch(`/api/learners?instructorId=${instructorId}`, { cache: 'no-store' }),
                fetch(`/api/instructors/${instructorId}`),
                fetch(`/api/bookings?instructorId=${instructorId}`),
                fetch(`/api/instructor-logs/${instructorId}`, { cache: 'no-store' })
            ]);

            if (lRes.ok) {
                const lData = await lRes.json();
                setLearners(lData.map(l => ({
                    ...l,
                    training_category: l.training_category || "Car (Manual)",
                    skills: typeof l.skills === 'string' ? JSON.parse(l.skills) : (l.skills || {})
                })));
            }

            if (logsRes.ok) {
                const allLogs = await logsRes.json();
                setAllLogsCache(allLogs);
            }

           if (iRes.ok) {
                const iData = await iRes.json();
                if (iData.needs_password_reset === 1) setNeedsPasswordReset(true);
                setInstructorName(iData.name || '');
                setInstructorDob(iData.date_of_birth || '');
                setInstructorBadgeNumber(iData.dvsa_badge_number || '');
                setWorkStart(iData.work_start_time?.substring(0, 5) || "09:00");
                setWorkEnd(iData.work_end_time?.substring(0, 5) || "17:00");
                setCancellationLockHours(iData.cancellation_lock_hours ?? 24);
setLessonBufferMinutes(iData.lesson_buffer_minutes ?? 0); // 👈 Add this line here!                
setInstructorEmail(iData.email || '');
                setInstructorPhone(iData.phone || '');
                setInstructorAddress(iData.address || '');
                setInstructorPostcode(iData.postcode || '');
                setHqPostcode(iData.postcode || ''); // 👈 Ensure this sets the editable postcode state
                setTravelRadius(iData.travel_radius_miles || 5);
            let parsedAreas = [];
        try {
            parsedAreas = typeof iData.selected_areas === 'string' 
                ? JSON.parse(iData.selected_areas) 
                : (Array.isArray(iData.selected_areas) ? iData.selected_areas : []);
        } catch (e) {
            parsedAreas = [];
        }
        setSelectedAreas(parsedAreas);

        try {
            const transmission = typeof iData.transmission_type === 'string' 
                ? JSON.parse(iData.transmission_type) 
                : (iData.transmission_type || ["Car (Manual)"]);
            
            const cats = Array.isArray(transmission) ? transmission : [transmission];
            setVehicleCategories(cats);
            
            // Automatically set default training category to the first available option
            if (cats.length > 0) {
                setTrainingCategory(cats[0]);
            }
        } catch (e) { 
            setVehicleCategories(["Car (Manual)"]); 
        }

                try {
                    const template = typeof iData.calendar_template === 'string' 
                        ? JSON.parse(iData.calendar_template) 
                        : (iData.calendar_template || []);
                    setTemplate(Array.isArray(template) ? template.map(b => ({ ...b, date: new Date(b.date) })) : []);
                } catch (e) { setTemplate([]); }
            }

            if (bRes.ok) {
                const bData = await bRes.json();
                setInstructorBookings(bData);
            }
        } catch (err) { 
            console.error("Critical DataFetch Error:", err); 
        }
    }, [instructorId]);

    useEffect(() => {
        if (instructorId) {
            fetchData();
        }
    }, [fetchData, instructorId]);

    useEffect(() => {
        if (hqPostcode && hqPostcode.replace(/\s/g, '').length >= 5) {
            const timer = setTimeout(() => {
                findNearbyAreas(hqPostcode, travelRadius);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [travelRadius, hqPostcode, findNearbyAreas]);

    // =================================================================
    // 5. BUSINESS LOGIC & HANDLERS
    // =================================================================

const triggerMasterConfigurationSave = async (drawerPayload = {}) => {
        setIsSaving(true);
        try {
            const res = await fetch(`/api/instructors/${instructorId}/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: instructorEmail,
                    phone: instructorPhone,
                    address: instructorAddress,
                    postcode: hqPostcode,
                    travel_radius_miles: parseInt(travelRadius, 10),
                    work_start_time: workStart,
                    work_end_time: workEnd,
                    cancellation_lock_hours: cancellationLockHours,
                    lesson_buffer_minutes: parseInt(lessonBufferMinutes, 10), // 👈 ADD THIS LINE HERE
                    calendar_template: template,
                    transmission_type: vehicleCategories,
                    selected_areas: selectedAreas,
                    date_of_birth: instructorDob,
                    dvsa_badge_number: instructorBadgeNumber,
                    name: drawerPayload.name || instructorName,
                    newPassword: drawerPayload.newPassword || null,
                    latitude: drawerPayload.latitude !== undefined ? drawerPayload.latitude : null,
                    longitude: drawerPayload.longitude !== undefined ? drawerPayload.longitude : null
                })
            });

            if (res.ok) {
                alert("🎉 Profile metadata and settings synced successfully!");
                setShowSettingsDrawer(false);
                fetchData();
            } else {
                const errorData = await res.json();
                alert(`❌ Sync failed: ${errorData.message || 'Unknown error'}`);
                fetchData();
            }
        } catch (err) { 
            console.error("Sync Error:", err);
            alert("❌ Sync failed: Could not connect to the server."); 
        } finally { 
            setIsSaving(false); 
        }
    };

const handleMoveBooking = async () => {
    if (!newBookingDate || !newBookingTime) {
        alert("Please select a new date and time.");
        return;
    }
    setIsProcessing(true);
    try {
        const response = await fetch(`/api/bookings/${selectedBooking.id}/reschedule`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                booking_date: newBookingDate,
                booking_time: newBookingTime,
                duration: selectedBooking.duration,
                pickup_location: selectedBooking.exact_pickup_address || selectedBooking.pickup_location
            })
        });
        if (!response.ok) throw new Error("Failed to reschedule booking");

    // 💬 AUTO-DISPATCH MESSAGE TO STUDENT
        await fetch('/api/messages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                instructor_id: parseInt(instructorId, 10),
                learner_id: parseInt(selectedBooking.learner_id, 10),
                sender_role: 'instructor',
                message_text: `📅 Lesson Rescheduled: Your lesson has been moved to ${newBookingDate} at ${newBookingTime}.`
            })
        });

        alert("✅ Lesson rescheduled and student notified via chat!");
        setShowManageModal(false);
        fetchData();
    } catch (err) {
        alert("❌ " + err.message);
    } finally {
        setIsProcessing(false);
    }
};




const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
        // 💬 AUTO-DISPATCH MESSAGE TO STUDENT BEFORE DELETING (so we have learner_id)
        if (selectedBooking && selectedBooking.learner_id) {
            await fetch('/api/messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructor_id: parseInt(instructorId, 10),
                    learner_id: parseInt(selectedBooking.learner_id, 10),
                    sender_role: 'instructor',
                    message_text: `❌ Lesson Cancelled: Your lesson on ${selectedBooking.booking_date?.slice(0, 10)} at ${selectedBooking.booking_time} has been cancelled.`
                })
            });
        }


        const response = await fetch(`/api/bookings/${bookingId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to cancel booking");

        alert("🗑️ Booking cancelled and student notified!");
        setShowManageModal(false);
        fetchData();
    } catch (err) {
        alert("❌ " + err.message);
    }
};



const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastText.trim()) return;
    if (!window.confirm("Send this announcement message to ALL your registered students?")) return;

    setIsBroadcasting(true);
    try {
       const res = await fetch('/api/messages/broadcast', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        instructor_id: parseInt(instructorId, 10),
        message_text: broadcastText.trim()
    })
});

        const data = await res.json();
        if (res.ok) {
            alert(`🎉 ${data.message}`);
            setBroadcastText('');
        } else {
            alert(`❌ ${data.error || 'Failed to send broadcast'}`);
        }
    } catch (err) {
        console.error("Broadcast connection error:", err);
        alert("❌ Could not connect to server for broadcast.");
    } finally {
        setIsBroadcasting(false);
    }
};






const handleRemoveBlock = async (targetDate, targetTime) => {
        const targetDateStr = getDateOnly(targetDate);
        
        // Helper to convert HH:MM to total minutes from midnight
        const timeToMins = (tStr) => {
            if (!tStr) return 0;
            const [h, m] = String(tStr).split(':').map(Number);
            return h * 60 + m;
        };

        const minsToTime = (totalMins) => {
            const h = Math.floor(totalMins / 60);
            const m = totalMins % 60;
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        };

        // Gather all blocked slots for this specific date
        const dayBlocks = template.filter(t => {
            if (!t || t.type !== 'blocked') return false;
            const tDateStr = getDateOnly(t.dateString || t.date);
            return tDateStr === targetDateStr;
        });

        const targetMins = timeToMins(targetTime);
        const clickedBlock = dayBlocks.find(t => {
            const startM = timeToMins(t.startTime);
            const endM = timeToMins(t.endTime);
            return targetMins >= startM && targetMins < endM;
        });

        if (!clickedBlock) return;

        let clusterStartMins = timeToMins(clickedBlock.startTime);
        let clusterEndMins = timeToMins(clickedBlock.endTime);

        // Expand backwards to find contiguous 30-min blocks
        let checkMins = clusterStartMins - 30;
        while (checkMins >= 0) {
            const timeStr = minsToTime(checkMins);
            const found = dayBlocks.find(t => timeToMins(t.startTime) === checkMins);
            if (found) {
                clusterStartMins = checkMins;
                checkMins -= 30;
            } else {
                break;
            }
        }

        // Expand forwards to find contiguous 30-min blocks
        checkMins = clusterEndMins;
        while (checkMins < 24 * 60) {
            const found = dayBlocks.find(t => timeToMins(t.startTime) === checkMins);
            if (found) {
                clusterEndMins = timeToMins(found.endTime);
                checkMins = clusterEndMins;
            } else {
                break;
            }
        }

        const startStr = minsToTime(clusterStartMins);
        const endStr = minsToTime(clusterEndMins);

        if (window.confirm(`Remove entire time-off block from ${startStr} to ${endStr} on ${targetDate.toLocaleDateString()}?`)) {
            // Filter out the entire expanded block cluster
            const updatedTemplate = template.filter(t => {
                if (!t || t.type !== 'blocked') return true;
                const tDateStr = getDateOnly(t.dateString || t.date);
                if (tDateStr !== targetDateStr) return true;

                const tStartM = timeToMins(t.startTime);
                // Drop any slot that falls within this cluster range
                const isWithinCluster = tStartM >= clusterStartMins && tStartM < clusterEndMins;
                return !isWithinCluster;
            });

            // Instant UI update
            setTemplate(updatedTemplate);

            try {
                const res = await fetch(`/api/instructors/${instructorId}/settings`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: instructorEmail,
                        phone: instructorPhone,
                        address: instructorAddress,
                        postcode: instructorPostcode,
                        travel_radius_miles: parseInt(travelRadius, 10),
                        work_start_time: workStart,
                        work_end_time: workEnd,
                        cancellation_lock_hours: cancellationLockHours,
lesson_buffer_minutes: parseInt(lessonBufferMinutes, 10), // 👈 ADD THIS LINE HERE
                        calendar_template: updatedTemplate,
                        transmission_type: vehicleCategories,
                        selected_areas: selectedAreas,
                        date_of_birth: instructorDob,
                        dvsa_badge_number: instructorBadgeNumber
                    })
                });

                if (res.ok) {
                    fetchData();
                    alert("✅ Entire time-off block removed successfully!");
                } else {
                    alert("❌ Failed to sync block removal with database.");
                }
            } catch (err) {
                console.error("Network error removing block:", err);
                alert("❌ Server connection error while removing block.");
            }
        }
    };
  

const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedBooking) return;
    try {
        await fetch('/api/messages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                instructor_id: parseInt(instructorId, 10),
                learner_id: parseInt(selectedBooking.learner_id, 10),
                sender_role: 'instructor',
                message_text: messageText.trim()
            })
        });
        alert(`✉️ Message sent successfully to ${selectedBooking.learner_name}!`);
        setMessageText('');
        setActionType('view');
    } catch (err) {
        console.error("Error sending message:", err);
        alert("❌ Failed to send message.");
    }
};



    const weekDates = useMemo(() => {
        const days = [];
        const tempDate = new Date(currentWeekStart);
        const day = tempDate.getDay();
        const diff = tempDate.getDate() - day + (day === 0 ? -6 : 1); 
        const monday = new Date(tempDate.setDate(diff));
        for (let i = 0; i < 7; i++) { 
            days.push(new Date(monday)); 
            monday.setDate(monday.getDate() + 1); 
        }
        return days;
    }, [currentWeekStart]);

    const handleCommitCompleteLessonReport = async () => {
        if (!selectedStudent) return;
        setIsSaving(true);
        
        const validLearner = learners.find(l => 
            l.name?.toLowerCase().trim() === selectedStudent.name?.toLowerCase().trim() ||
            parseInt(l.id, 10) === parseInt(selectedStudent.id, 10)
        );

        if (!validLearner) {
            alert(`❌ Error: Student not found in active roster.`);
            setIsSaving(false);
            return;
        }

        const secureLearnerId = validLearner.id;

        try {
            await fetch(`/api/learners/${secureLearnerId}/skills`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ skills: selectedStudent.skills })
            });

            await fetch(`/api/learners/${secureLearnerId}/readiness-checklist`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ checklist: readinessChecklist })
            });

            const logRes = await fetch('/api/lesson-logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructor_id: parseInt(instructorId, 10),
                    learner_id: parseInt(secureLearnerId, 10),
                    lesson_date: logDate || new Date().toISOString().slice(0, 10),
                    duration_minutes: parseInt(logDuration, 10) || 60,
                    custom_notes: customLogNotes || '',
                    next_steps: nextStepsPlan || '',
                    progress_score: parseInt(logProgressScore, 10) || 1
                })
            });

            if (logRes.ok) {
                alert("🎉 Lesson report, skills, and readiness targets successfully saved!");
                await fetchData(); 
                setShowProgressModal(false);
            } else {
                throw new Error("Failed to save lesson log");
            }
        } catch (err) {
            console.error("Save Error:", err);
            alert("❌ Save failed: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };




// 🔑 Date helper for consistent string matching
    const getDateOnly = (value) => {
        if (!value) return null;
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return null;
            return [
                value.getFullYear(),
                String(value.getMonth() + 1).padStart(2, '0'),
                String(value.getDate()).padStart(2, '0')
            ].join('-');
        }
        const d = new Date(value);
        if (!Number.isNaN(d.getTime())) {
            return [
                d.getFullYear(),
                String(d.getMonth() + 1).padStart(2, '0'),
                String(d.getDate()).padStart(2, '0')
            ].join('-');
        }
        const str = String(value).trim();
        const directMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (directMatch) {
            return `${directMatch[1]}-${directMatch[2]}-${directMatch[3]}`;
        }
        return null;
    };

const handleSaveSlot = async (type) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selection.date < today) {
            alert("❌ Past weeks are locked and cannot be modified.");
            setShowSlotModal(false);
            return;
        }

        const dragDateStr = getDateOnly(selection.date);
        
        // Parse start and end times into exact minutes from midnight
        const [startH, startM] = selection.startTime.split(':').map(Number);
        const [endH, endM] = selection.endTime.split(':').map(Number);
        const startTotalMins = startH * 60 + startM;
        const endTotalMins = endH * 60 + endM;

        // 🛡️ Prevent blocking over existing student bookings
        if (type === 'blocked') {
            const hasBookingConflict = instructorBookings?.some(b => {
                const bDate = getDateOnly(b.booking_date || b.date);
                if (bDate !== dragDateStr || b.status === 'cancelled') return false;
                const [bStartH, bStartM] = String(b.booking_time || '00:00').split(':').map(Number);
                const bStartMins = bStartH * 60 + bStartM;
                const bDurMins = Math.round(parseFloat(b.duration || 1) * 60);
                const bEndMins = bStartMins + bDurMins;
                
                return startTotalMins < bEndMins && endTotalMins > bStartMins;
            });

            if (hasBookingConflict) {
                alert("❌ Cannot block time-off: There is a confirmed student booking during this time range.");
                setShowSlotModal(false);
                return;
            }
        }

        // Filter out any overlapping blocks on that day so we can replace them cleanly with the exact range
        const filtered = template.filter(t => {
            if (!t || t.type !== 'blocked') return true;
            const tDateStr = getDateOnly(t.dateString || t.date);
            if (tDateStr !== dragDateStr) return true;
            
            const [tH1, tM1] = String(t.startTime).split(':').map(Number);
            const [tH2, tM2] = String(t.endTime).split(':').map(Number);
            const tStartMins = tH1 * 60 + tM1;
            const tEndMins = tH2 * 60 + tM2;

            return !(startTotalMins < tEndMins && endTotalMins > tStartMins);
        });
        
        let finalTemplate = filtered;
        if (type === 'blocked') {
            // 🔑 Save as ONE single precise block spanning the exact start and end times chosen
            const newBlock = {
                date: selection.date,
                dateString: dragDateStr,
                startTime: selection.startTime,
                endTime: selection.endTime,
                type: 'blocked',
                id: Math.random()
            };
            finalTemplate = [...filtered, newBlock];
        }

        setTemplate(finalTemplate);
        setShowSlotModal(false);
        setSelection(null);

        try {
            const res = await fetch(`/api/instructors/${instructorId}/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: instructorEmail,
                    phone: instructorPhone,
                    address: instructorAddress,
                    postcode: instructorPostcode,
                    travel_radius_miles: parseInt(travelRadius, 10),
                    work_start_time: workStart,
                    work_end_time: workEnd,
                    cancellation_lock_hours: cancellationLockHours,
lesson_buffer_minutes: parseInt(lessonBufferMinutes, 10), // 👈 ADD THIS LINE HERE
                    calendar_template: finalTemplate,
                    transmission_type: vehicleCategories,
                    selected_areas: selectedAreas,
                    date_of_birth: instructorDob,
                    dvsa_badge_number: instructorBadgeNumber
                })
            });

            if (!res.ok) {
                console.error("Failed to persist calendar blocks to backend");
            }
        } catch (err) {
            console.error("Network error while saving slot blocks:", err);
        }
    };



    const handleOpenTrackingPortal = async (studentOrBooking, bookedDuration = 60, specificDate = null) => {
        const masterStudent = learners.find(l => 
            l.name?.toLowerCase().trim() === studentOrBooking.name?.toLowerCase().trim() ||
            parseInt(l.id, 10) === parseInt(studentOrBooking.id, 10) ||
            parseInt(l.id, 10) === parseInt(studentOrBooking.learner_id, 10)
        ) || studentOrBooking;

        let isAlreadyLogged = false;
        
        const targetDate = specificDate || studentOrBooking.booking_date?.slice(0, 10) || new Date().toISOString().slice(0, 10);
        setLogDate(targetDate);
        setLogDuration(bookedDuration ? parseInt(bookedDuration, 10) : 60);

        setCustomLogNotes('');
        setNextStepsPlan('');
        setExpandedCategory(null); 
        
        let checkArray = [];
        if (masterStudent.test_readiness_checklist) {
            checkArray = typeof masterStudent.test_readiness_checklist === 'string' ? JSON.parse(masterStudent.test_readiness_checklist) : masterStudent.test_readiness_checklist;
        }
        setReadinessChecklist(Array.isArray(checkArray) ? checkArray : []);

        try {
            const res = await fetch(`/api/lesson-logs/${masterStudent.id}`);
            if (res.ok) {
                const data = await res.json();
                setHistoricalLogs(data);

                const existingLogForDate = data.find(l => l.lesson_date?.slice(0, 10) === targetDate);
                if (existingLogForDate) {
                    setCustomLogNotes(existingLogForDate.custom_notes || '');
                    setNextStepsPlan(existingLogForDate.next_steps || '');
                    setLogProgressScore(existingLogForDate.progress_score || 3);
                    isAlreadyLogged = true;
                }
            }
        } catch (err) { console.error(err); }
        
        setSelectedStudent({ ...masterStudent, isReadOnlyAudit: isAlreadyLogged });
        setShowProgressModal(true);
    };

    const toggleChecklistItem = (item) => {
        setReadinessChecklist(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
    };

    const calculateProgress = (skills = {}) => {
        const targetFramework = DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent?.training_category || "Car (Manual)"];
        const totalSkillsCount = Object.values(targetFramework || {}).flat().length || 1;
        const safeSkills = skills || {};
        const totalAccumulatedScore = Object.values(safeSkills).reduce((sum, current) => sum + current, 0);
        return Math.round((totalAccumulatedScore / (totalSkillsCount * 5)) * 100) || 0;
    };

    const handleStatusChange = async (id, newStatus) => {
        setLearners(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
        await fetch(`/api/learners/${id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
    };

    const handleRemoveStudent = async (id) => {
        if (window.confirm("Remove this student?")) {
            await fetch(`/api/learners/${id}`, { method: 'DELETE' });
            await fetchData();
        }
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();
        await fetch('/api/learners', { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify({ 
                name: studentName, email: studentEmail, instructor_id: instructorId 
            }) 
        });
        setStudentName(''); setStudentEmail('');
        await fetchData(); 
    };

    const getInstructorMaxAllowedDuration = () => {
        if (!selection) return 1;
        const startHour = parseInt(selection.startTime.split(':')[0], 10);
        const workEndHour = workEnd ? parseInt(workEnd.split(':')[0], 10) : 18;
        
        let maxHours = workEndHour - startHour;
        if (maxHours < 1) maxHours = 1;

        const dragDateStr = selection.date ? selection.date.toISOString().slice(0, 10) : '';

        for (let h = startHour + 1; h < workEndHour; h++) {
            const hourStr = `${h.toString().padStart(2, '0')}:00`;
            
            const isReserved = instructorBookings?.some(b => {
                const bDate = b.booking_date ? b.booking_date.slice(0, 10) : '';
                if (bDate !== dragDateStr) return false;
                const bStart = parseInt(String(b.booking_time || '00:00').substring(0, 2), 10);
                const bDur = parseInt(b.duration || 1, 10);
                return h >= bStart && h < (bStart + bDur);
            });

            if (isReserved) {
                maxHours = h - startHour;
                break;
            }
        }

        return Math.min(6, Math.max(1, maxHours));
    };

 const handleDirectInstructorBooking = async () => {
        if (!slotSelectedLearnerId || !selection) {
            alert("Please select a student to book.");
            return;
        }

        const duration = parseInt(selection.endTime.split(':')[0], 10) - parseInt(selection.startTime.split(':')[0], 10);
        const maxAllowed = getInstructorMaxAllowedDuration();
        
        if (duration > maxAllowed) {
            alert(`❌ Selected duration exceeds working hours or conflicts with existing bookings (Max allowed: ${maxAllowed} hours).`);
            return;
        }

        const targetLearner = learners.find(l => parseInt(l.id, 10) === parseInt(slotSelectedLearnerId, 10));
        const resolvedAddress = slotCustomPickup || targetLearner?.default_pickup_address || targetLearner?.address || null;

        try {
            const response = await fetch('/api/bookings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructor_id: instructorId,
                    learner_id: slotSelectedLearnerId,
                    learner_name: targetLearner?.name || 'Student',
                    booking_date: selection.date.toISOString().slice(0, 10),
                    booking_time: selection.startTime,
                    duration: duration > 0 ? duration : 1,
                    pickup_type: slotPickupType,
                    exact_pickup_address: resolvedAddress,
                    pickup_location: resolvedAddress // 🔑 Added this so backend calendar template catches the address instantly!
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || "Failed to secure booking.");
            }

            alert("🎉 Lesson successfully booked with custom pickup!");
            setShowSlotModal(false);
            setSelection(null);
            fetchData();
        } catch (err) {
            alert("❌ " + err.message);
        }
    };

// 🖨️ SEPARATE REPORT 1: Individual Student Report (Brighter Traffic Lights & Larger Donuts)
    const handleGeneratePrintReport = () => {
        if (!selectedStudent) return;

        const printWindow = window.open('', '_blank');
        const studentFramework = DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent.training_category || "Car (Manual)"] || {};
        const progressPercent = calculateProgress(selectedStudent.skills);

        // 🕒 Calculate Done Hours vs Booked Hours
        const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(selectedStudent.id, 10));
        const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
        const doneHours = Math.round(doneMinutes / 60);

        const todayStr = new Date().toISOString().slice(0, 10);
        const studentBookings = instructorBookings.filter(b => 
            (parseInt(b.learner_id, 10) === parseInt(selectedStudent.id, 10) || b.learner_name?.toLowerCase() === selectedStudent.name?.toLowerCase()) &&
            b.status === 'confirmed' &&
            String(b.booking_date).slice(0, 10) >= todayStr
        );
        const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

        // 📋 Readiness metrics
        const totalChecklist = MASTER_READINESS_CHECKLIST.length;
        const achievedChecklist = readinessChecklist.length;
        const checklistPercent = Math.round((achievedChecklist / totalChecklist) * 100) || 0;
        const overallReadiness = Math.round((progressPercent + checklistPercent) / 2);

        let statusBadgeBg = '#ef4444';
        let statusText = 'Foundation Stage (Needs Development)';
        if (overallReadiness >= 80) {
            statusBadgeBg = '#22c55e';
            statusText = '🌟 Test Ready / Exam Standard';
        } else if (overallReadiness >= 50) {
            statusBadgeBg = '#eab308';
            statusText = '📈 Intermediate Progress';
        }

        // 🚦 Brighter Traffic Light Color Helper Function
        const getTrafficLightColor = (percent) => {
            if (percent >= 80) return '#22c55e'; // Bright Green
            if (percent >= 50) return '#eab308'; // Bright Amber/Yellow
            return '#ef4444';                     // Bright Red
        };

        // SVG Donut Chart helper function (optimized for crisp rendering)
        const getDonutSvg = (percent, size = 88, stroke = 9) => {
            const radius = 35;
            const circumference = 2 * Math.PI * radius;
            const strokeDashoffset = circumference - (percent / 100) * circumference;
            const color = getTrafficLightColor(percent);

            return `
                <svg width="${size}" height="${size}" viewBox="0 0 100 100" style="transform: rotate(-90deg);">
                    <circle cx="50" cy="50" r="${radius}" stroke="#e2e8f0" stroke-width="${stroke}" fill="transparent" />
                    <circle cx="50" cy="50" r="${radius}" stroke="${color}" stroke-width="${stroke}" stroke-dasharray="${circumference}" stroke-dashoffset="${strokeDashoffset}" stroke-linecap="round" fill="transparent" />
                </svg>
            `;
        };

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Student Progress Summary - ${selectedStudent.name}</title>
                <style>
                    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; padding: 25px; line-height: 1.4; max-width: 850px; margin: 0 auto; background: #fff; }
                    .report-header { display: flex; justify-content: space-between; border-bottom: 3px solid #0066cc; padding-bottom: 12px; margin-bottom: 20px; }
                    h1 { color: #0066cc; margin: 0 0 3px 0; font-size: 20px; }
                    .card { background: #f8f9fa; border: 1px solid #cbd5e0; border-radius: 8px; padding: 15px; margin-bottom: 15px; }
                    .meta-grid { display: flex; gap: 10px; font-size: 12px; margin-top: 10px; }
                    .meta-item { flex: 1; background: #ffffff; padding: 10px; border-radius: 6px; border: 1px solid #e2e8f0; text-align: center; }
                    
                    /* Larger Charts Grid Layout */
                    .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 12px; }
                    .chart-card { background: #ffffff; border: 1px solid #cbd5e0; border-radius: 8px; padding: 15px 10px; text-align: center; }
                    .donut-wrapper { position: relative; width: 88px; height: 88px; margin: 0 auto 8px auto; }
                    .donut-text { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-weight: bold; font-size: 14px; color: #1e293b; }

                    .badge { display: inline-block; padding: 3px 8px; font-size: 11px; font-weight: bold; border-radius: 4px; background: #e2e8f0; }
                    .passed { background: #22c55e; color: #fff; }
                    
                    /* Traffic Light Legend */
                    .legend { display: flex; justify-content: center; gap: 25px; font-size: 11px; margin-top: 8px; font-weight: bold; color: #334155; }
                    .legend-item { display: flex; align-items: center; gap: 6px; }
                    .dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }

                    .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #64748b; border-top: 1px solid #cbd5e0; padding-top: 8px; }
                    
                    @media print {
                        body { padding: 10px; }
                        button { display: none; }
                        .charts-grid { page-break-inside: avoid; }
                    }
                </style>
            </head>
            <body>
                <div class="report-header">
                    <div>
                        <h1>🚗 Independent Driving School</h1>
                        <p style="margin: 0; color: #64748b; font-size: 12px;">Official Student Training Summary & Performance Report</p>
                    </div>
                    <div style="text-align: right;">
                        <p style="margin: 0; font-weight: bold; font-size: 14px;">${selectedStudent.name}</p>
                        <p style="margin: 2px 0 0 0; font-size: 11px; color: #64748b;">Generated: ${new Date().toLocaleDateString('en-GB')}</p>
                    </div>
                </div>

                <!-- Summary Overview Card -->
                <div class="card">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                        <h3 style="margin: 0; color: #0066cc; font-size: 15px;">📊 Training Status & Volume Overview</h3>
                        <span style="padding: 3px 10px; background: ${statusBadgeBg}; color: #ffffff; border-radius: 6px; font-size: 11px; font-weight: bold;">${statusText}</span>
                    </div>
                    <div class="meta-grid">
                        <div class="meta-item">
                            <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">CATEGORY</span>
                            <strong style="font-size: 11px;">${selectedStudent.training_category || "Car (Manual)"}</strong>
                        </div>
                        <div class="meta-item">
                            <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">STATUS</span>
                            <span class="badge ${selectedStudent.status === 'Passed' ? 'passed' : ''}">${selectedStudent.status || 'Beginner'}</span>
                        </div>
                        <div class="meta-item">
                            <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">COMPLETED</span>
                            <strong style="color: #22c55e; font-size: 12px;">${doneHours}h Done</strong>
                        </div>
                        <div class="meta-item">
                            <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">BOOKED</span>
                            <strong style="color: #0066cc; font-size: 12px;">${bookedHours}h Booked</strong>
                        </div>
                    </div>
                </div>

                <!-- Overall Readiness & Syllabus Mastery Donut Cards -->
                <div class="card" style="background: #ffffff;">
                    <h3 style="margin: 0 0 10px 0; color: #0066cc; font-size: 14px; text-align: center;">🎯 Overall Mastery & Exam Gates</h3>
                    <div style="display: flex; justify-content: space-around; gap: 15px;">
                        <div style="text-align: center; flex: 1;">
                            <div class="donut-wrapper" style="width: 95px; height: 95px;">
                                ${getDonutSvg(progressPercent, 95, 10)}
                                <div class="donut-text" style="font-size: 14px;">${progressPercent}%</div>
                            </div>
                            <strong style="font-size: 12px; color: #1e293b;">Total Syllabus Mastery</strong>
                        </div>
                        <div style="text-align: center; flex: 1;">
                            <div class="donut-wrapper" style="width: 95px; height: 95px;">
                                ${getDonutSvg(checklistPercent, 95, 10)}
                                <div class="donut-text" style="font-size: 14px;">${checklistPercent}%</div>
                            </div>
                            <strong style="font-size: 12px; color: #1e293b;">Test-Readiness Gate (${achievedChecklist}/${totalChecklist})</strong>
                        </div>
                    </div>
                </div>

                <!-- 6 Core Categories Larger Donut Breakdown Grid with Bright Traffic Lights -->
                <div class="card" style="background: #ffffff;">
                    <h3 style="margin: 0 0 5px 0; color: #0066cc; font-size: 14px; text-align: center;">📁 6 Core Competency Category Breakdowns</h3>
                    <div class="legend">
                        <span class="legend-item"><span class="dot" style="background: #22c55e;"></span> Proficient (80%+)</span>
                        <span class="legend-item"><span class="dot" style="background: #eab308;"></span> Developing (50-79%)</span>
                        <span class="legend-item"><span class="dot" style="background: #ef4444;"></span> Needs Focus (&lt;50%)</span>
                    </div>

                    <div class="charts-grid">
                        ${Object.entries(studentFramework).map(([category, skills]) => {
                            const totalPossible = skills.length * 5;
                            const totalEarned = skills.reduce((sum, skill) => sum + (selectedStudent?.skills?.[skill] || 1), 0);
                            const catPercent = Math.round((totalEarned / totalPossible) * 100);

                            return `
                                <div class="chart-card">
                                    <div class="donut-wrapper">
                                        ${getDonutSvg(catPercent)}
                                        <div class="donut-text">${catPercent}%</div>
                                    </div>
                                    <strong style="font-size: 11px; color: #1e293b; display: block; line-height: 1.3; height: 28px; overflow: hidden; font-weight: 600;">${category}</strong>
                                </div>
                            `;
                        }).join('')}
                    </div>
                </div>

                <div class="footer">
                    <p>Independent Driving School Management Platform — Official Competency & Progress Report</p>
                </div>

                <script>
                    window.onload = function() { window.print(); }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
    };

 



// 🖨️ SEPARATE REPORT 2: All Students Weekly Hours Report
    const handleGenerateOverallFleetReport = () => {
        const totalWeeklyFleetHours = instructorBookings
            .filter(b => b.status === 'confirmed')
            .reduce((acc, b) => acc + (parseInt(b.duration, 10) || 1), 0);

        const formattedDob = instructorDob ? new Date(instructorDob).toLocaleDateString('en-GB') : 'Not Provided';
        const todayStr = new Date().toISOString().slice(0, 10);

        const printWindow = window.open('', '_blank');
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Overall Performance Report</title>
                <style>
                    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; padding: 30px; line-height: 1.5; max-width: 1000px; margin: 0 auto; }
                    .report-header { display: flex; justify-content: space-between; border-bottom: 3px solid #2f855a; padding-bottom: 15px; margin-bottom: 20px; }
                    h1 { color: #2f855a; margin: 0 0 5px 0; font-size: 22px; }
                    .card { background: #f8f9fa; border: 1px solid #cbd5e0; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
                    .compliance-table { width: 100%; border-collapse: collapse; margin-top: 5px; }
                    .compliance-table td { border: none; padding: 8px 0; font-size: 13px; width: 50%; vertical-align: top; }
                    table.roster { width: 100%; border-collapse: collapse; margin-top: 15px; }
                    table.roster th, table.roster td { border: 1px solid #cbd5e0; padding: 8px 10px; font-size: 11px; text-align: left; }
                    table.roster th { background: #2f855a; color: white; }
                    .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #64748b; border-top: 1px solid #cbd5e0; padding-top: 10px; }
                    @media print {
                        body { padding: 10px; }
                        button { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="report-header">
                    <div>
                        <h1>🚗 Independent Driving School</h1>
                        <p style="margin: 0; color: #64748b; font-size: 13px;">Overall All-Student Weekly Performance Summary</p>
                    </div>
                    <div style="text-align: right;">
                        <p style="margin: 0; font-weight: bold; font-size: 14px;">Instructor: ${instructorName || 'Certified Partner'}</p>
                        <p style="margin: 2px 0 0 0; font-size: 12px; color: #64748b;">Generated: ${new Date().toLocaleDateString('en-GB')}</p>
                    </div>
                </div>

                <div class="card">
                    <h3 style="margin-top: 0; color: #2f855a; border-bottom: 1px solid #cbd5e0; padding-bottom: 8px;">🛡️ Instructor Official Compliance Record</h3>
                    <table class="compliance-table">
                        <tr>
                            <td><b>Full Name:</b> ${instructorName || 'N/A'}</td>
                            <td><b>DVSA Badge Number:</b> ${instructorBadgeNumber || 'Pending'}</td>
                        </tr>
                        <tr>
                            <td><b>Date of Birth:</b> ${formattedDob}</td>
                            <td><b>Active Roster Count:</b> ${learners.length} Students</td>
                        </tr>
                    </table>
                </div>

                <div class="card" style="background: #f0fdf4; border-color: #bbf7d0;">
                    <h3 style="margin-top: 0; color: #166534;">📊 Overall Weekly Teaching Volume</h3>
                    <p style="font-size: 15px; margin: 0; color: #14532d;">
                        Total Confirmed Teaching Hours Logged: <b>${totalWeeklyFleetHours} Hours</b> across all students on active schedule.
                    </p>
                </div>

                <h3>Active Student Roster Overview</h3>
                <table class="roster">
                    <thead>
                        <tr>
                            <th>Student Name</th>
                            <th>Telephone</th>
                            <th>Email Address</th>
                            <th>Postcode</th>
                            <th style="text-align: center;">Completed</th>
                            <th style="text-align: center;">Booked</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${learners.length === 0 ? '<tr><td colspan="7" style="text-align:center;">No students registered.</td></tr>' : 
                            learners.map(l => {
                                const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
                                const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
                                const doneHours = Math.round(doneMinutes / 60);

                                const studentBookings = instructorBookings.filter(b => 
                                    (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
                                    b.status === 'confirmed' &&
                                    String(b.booking_date).slice(0, 10) >= todayStr
                                );
                                const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

                                const phoneDisplay = l.phone_number || l.phone || 'N/A';
                                const emailDisplay = l.email || 'N/A';
                                const addressPostcode = l.default_pickup_address || l.address || '';
                                // Extract postcode or show area snippet if available
                                const postcodeDisplay = addressPostcode ? addressPostcode.split(',').pop().trim() : 'N/A';

                                return `
                                    <tr>
                                        <td><b>${l.name}</b></td>
                                        <td>${phoneDisplay}</td>
                                        <td>${emailDisplay}</td>
                                        <td>${postcodeDisplay}</td>
                                        <td style="text-align: center; color: #2f855a; font-weight: bold;">${doneHours}h</td>
                                        <td style="text-align: center; color: #0066cc; font-weight: bold;">${bookedHours}h</td>
                                        <td><b>${l.status || 'Beginner'}</b></td>
                                    </tr>
                                `;
                            }).join('')
                        }
                    </tbody>
                </table>

                <div class="footer">
                    <p>Independent Driving School Management Platform — Overall Compliance & Summary Report</p>
                </div>

                <script>
                    window.onload = function() { window.print(); }
                </script>
            </body>
            </html>
        `;
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
    };





// 📁 NEW: CSV Export Function for All Students Roster
    const exportAllStudentsCSV = () => {
        const todayStr = new Date().toISOString().slice(0, 10);
        
        const headers = ["Student Name", "Telephone", "Email Address", "Postcode", "Completed Hours", "Booked Hours", "Status"];
        
        const rows = learners.map(l => {
            const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
            const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
            const doneHours = Math.round(doneMinutes / 60);

            const studentBookings = instructorBookings.filter(b => 
                (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
                b.status === 'confirmed' &&
                String(b.booking_date).slice(0, 10) >= todayStr
            );
            const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

            const rawPhone = l.phone_number || l.phone || '';
            const phoneDisplay = rawPhone ? `="${rawPhone}"` : '"N/A"';
            const emailDisplay = l.email || 'N/A';
            const addressPostcode = l.default_pickup_address || l.address || '';
            const postcodeDisplay = addressPostcode ? addressPostcode.split(',').pop().trim() : 'N/A';

            return [
                `"${l.name}"`,
                phoneDisplay,
                `"${emailDisplay}"`,
                `"${postcodeDisplay}"`,
                `${doneHours}h`,
                `${bookedHours}h`,
                `"${l.status || 'Beginner'}"`
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8," + 
            [headers.join(","), ...rows.map(e => e.join(","))].join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `LessonLane_Student_Roster_${todayStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

  // =================================================================
    // 6. RENDER UI
    // =================================================================

    return (
        <div style={Styles.containerStyle}>
            {needsPasswordReset && (
                <div style={Styles.lockOverlay}>
                    <div style={Styles.lockModal}>
                        <h2 style={{ color: '#111111' }}>🔒 Secure Password Change</h2>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Pick new password" style={Styles.inputStyle} />
                        <button onClick={async () => {
                            const res = await fetch(`/api/instructors/${instructorId}/first-password`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ newPassword }) });
                            if (res.ok) { setNeedsPasswordReset(false); fetchData(); }
                        }} style={Styles.saveTemplateBtn}>Unlock Portal</button>
                    </div>
                </div>
            )}




<div style={Styles.headerStyle}>
                <img src={instructorLogo} alt="Logo" style={Styles.logoStyle} />
                <h1 style={{ color: '#111111', margin: 0, fontWeight: '800' }}>
                    {instructorName ? `${instructorName}'s Dashboard` : 'Instructor Dashboard'}
                </h1>
                
                {/* 📊 Report Format Dropdown & Action Group */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <select 
                        id="reportFormatSelect"
                        style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e0',
                            backgroundColor: '#fff',
                            fontSize: '13px',
                            color: '#2d3748',
                            cursor: 'pointer',
                            height: '38px',
                            fontWeight: '500'
                        }}
                    >
                        <option value="print">📊 Print Summary Report</option>
                        <option value="csv">📁 Export as .CSV</option>
                    </select>

                    <button 
                        onClick={() => {
                            const format = document.getElementById('reportFormatSelect').value;
                            if (format === 'print') {
                                handleGenerateOverallFleetReport();
                            } else {
                                exportAllStudentsCSV();
                            }
                        }} 
                        style={{ ...Styles.navBtn, background: '#2f855a', border: 'none', color: '#ffffff', fontWeight: 'bold', height: '38px', cursor: 'pointer' }}
                    >
                        Generate
                    </button>
                </div>

                {/* ❓ Help Guide Button */}
                <button 
                    onClick={() => setIsHelpOpen(true)}
                    style={{ ...Styles.navBtn, background: '#475569', border: 'none', color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                    ❓ Help Guide
                </button>

           


                <button onClick={() => setShowSettingsDrawer(true)} style={{ ...Styles.navBtn, background: '#f8f9fa', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold' }}>⚙️ Profile Settings</button>
                <button onClick={() => { localStorage.clear(); navigate('/'); }} style={{ ...Styles.navBtn, backgroundColor: '#e53e3e', color: '#ffffff', fontWeight: 'bold', border: 'none' }}>Logout 🚪</button>
            </div>



            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h3 style={{ margin: 0, color: '#111111' }}>📅 Weekly Calendar</h3>
                    <button onClick={() => { 
                        const d = new Date(currentWeekStart); 
                        d.setDate(d.getDate() - 7); 
                        setCurrentWeekStart(new Date(d)); 
                    }} style={Styles.navBtn}>◀ Prev</button>
                    
                    <button onClick={() => setCurrentWeekStart(new Date())} style={Styles.navBtn}>Today</button>
                    
                    <button onClick={() => { 
                        const d = new Date(currentWeekStart); 
                        d.setDate(d.getDate() + 7); 
                        setCurrentWeekStart(new Date(d)); 
                    }} style={Styles.navBtn}>Next ▶</button>
                </div>
               
            </div>

        

<div style={{ ...Styles.cardStyle, background: '#ffffff', border: '1px solid #cbd5e0', padding: '15px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px', color: '#111111' }}>
        
        {/* Working Hours Group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <strong style={{ color: '#111111', whiteSpace: 'nowrap' }}>🕒 Set your Working Hours:</strong>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#4a5568', fontWeight: '600' }}>Start:</span>
                <select value={workStart} onChange={e => setWorkStart(e.target.value)} style={Styles.selectStyle}>
                    {Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#4a5568', fontWeight: '600' }}>End:</span>
                <select value={workEnd} onChange={e => setWorkEnd(e.target.value)} style={Styles.selectStyle}>
                    {Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
            </div>
        </div>

        {/* Cancellation Period, Buffer, & Save Group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#4a5568', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>🛑 Cancellation Period:</span>
                <select value={cancellationLockHours} onChange={e => setCancellationLockHours(parseInt(e.target.value, 10))} style={Styles.selectStyle}>
                    <option value="0">Immediate</option>
                    <option value="24">24 Hours Notice</option>
                    <option value="48">48 Hours Notice</option>
                </select>
            </div>

            {/* 🔑 New Lesson Buffer Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#4a5568', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>⏳ Lesson Buffer:</span>
<select value={lessonBufferMinutes} onChange={e => setLessonBufferMinutes(parseInt(e.target.value, 10))} style={Styles.selectStyle}>
    <option value={0}>None (0m)</option>
    <option value={15}>15 Minutes</option>
    <option value={30}>30 Minutes</option>
    <option value={45}>45 Minutes</option>
    <option value={60}>1 Hour</option>
</select>
            </div>

            <button onClick={triggerMasterConfigurationSave} style={{ ...Styles.saveTemplateBtn, margin: 0, width: 'auto' }}>
                {isSaving ? 'Syncing...' : '💾 Save Changes'}
            </button>
        </div>

    </div>
</div>

{/* --- SIDE-BY-SIDE: CALENDAR (LEFT) & DISPATCH TICKER (RIGHT) --- */}
<div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
    
    {/* Left: Calendar takes up primary space */}
    <div style={{ flex: '1 1 650px', minWidth: '0', maxWidth: '100%', boxSizing: 'border-box' }}>
<BookingCalendar 
            instructor={instructorId}
            template={template}
            instructorBookings={instructorBookings}
            timeLabels={timeLabels}
            weekDates={weekDates}
            learners={learners}
            role="instructor"
            workStart={workStart}
            workEnd={workEnd}
            allLogsCache={allLogsCache}
            onBookingSuccess={fetchData}
            onCellClick={(date, time, isStudentReserved, liveBooking, isLessonInPast, hasLessonBeenLogged, isBlockedCellClick) => {
                
                // 🔑 Directly catch clicks on red instructor time-off blocks
                if (isBlockedCellClick) {
                    if (window.confirm(`Do you want to remove the time-off block for ${date.toLocaleDateString()} at ${time}?`)) {
                        handleRemoveBlock(date, time);
                    }
                    return;
                }

                const isBlockedCell = Array.isArray(template) && template.some(t => {
                    if (!t) return false;
                    const tDate = t.dateString || t.date;
                    const matchesDate = tDate instanceof Date ? tDate.toDateString() === date.toDateString() : tDate === date.toDateString();
                    return matchesDate && time >= (t.startTime || '') && time < (t.endTime || '') && t.type === 'blocked';
                });

                if (isBlockedCell) {
                    if (window.confirm(`Do you want to remove the time-off block for ${date.toLocaleDateString()} at ${time}?`)) {
                        handleRemoveBlock(date, time);
                    }
                    return;
                }

                if (isStudentReserved) {
                    if (isLessonInPast) {
                        if (hasLessonBeenLogged) {
                            alert("🔒 This past lesson has already been logged and is locked for auditing.");
                            return;
                        }

                        const matchedStudent = learners.find(l => 
                            parseInt(l.id, 10) === parseInt(liveBooking.learner_id, 10) ||
                            l.name?.toLowerCase().trim() === liveBooking.learner_name?.toLowerCase().trim()
                        );

                        if (!matchedStudent) {
                            alert("❌ Error: Could not find matching student record for this booking.");
                            return;
                        }

                        const exactBookingDate = liveBooking.booking_date 
                            ? String(liveBooking.booking_date).slice(0, 10) 
                            : [
                                date.getFullYear(),
                                String(date.getMonth() + 1).padStart(2, '0'),
                                String(date.getDate()).padStart(2, '0')
                              ].join('-');
                            
                        const bookingDurationMins = liveBooking.duration ? parseInt(liveBooking.duration, 10) * 60 : 60;
                        handleOpenTrackingPortal(matchedStudent, bookingDurationMins, exactBookingDate);
                    } else {
                        const matchedLearner = learners.find(l => 
                            parseInt(l.id, 10) === parseInt(liveBooking.learner_id, 10) ||
                            l.name?.toLowerCase().trim() === liveBooking.learner_name?.toLowerCase().trim()
                        );

                        let addressToDisplay = matchedLearner?.default_pickup_address || matchedLearner?.address || null;
                        let addressTypeToDisplay = matchedLearner?.default_pickup_type || 'home';

                        if (
                            liveBooking.pickup_type === 'secondary' || 
                            liveBooking.pickup_location === 'secondary' ||
                            liveBooking.pickup_location?.toLowerCase().includes('secondary')
                        ) {
                            if (matchedLearner?.secondary_pickup) {
                                addressToDisplay = matchedLearner.secondary_pickup;
                                addressTypeToDisplay = matchedLearner.secondary_type || 'secondary';
                            }
                        } else if (liveBooking.exact_pickup_address) {
                            addressToDisplay = liveBooking.exact_pickup_address;
                            addressTypeToDisplay = liveBooking.pickup_type || 'custom';
                        } else if (liveBooking.pickup_location && liveBooking.pickup_location !== 'Standard Student Address' && liveBooking.pickup_location !== matchedLearner?.default_pickup_address) {
                            addressToDisplay = liveBooking.pickup_location;
                            addressTypeToDisplay = 'location';
                        }

                        setSelectedBooking({
                            ...liveBooking,
                            student_default_address: addressToDisplay,
                            student_default_type: addressTypeToDisplay,
                            primary_address: matchedLearner?.default_pickup_address || matchedLearner?.address || 'Home Address',
                            secondary_address: matchedLearner?.secondary_pickup || null,
                            secondary_label: matchedLearner?.secondary_type ? matchedLearner.secondary_type.toUpperCase() : 'WORK / SCHOOL',
                            pickup_location: liveBooking.pickup_location || addressToDisplay || 'Home Address'
                        });

                        setActionType('view');
                        setMessageText(`Hi ${liveBooking.learner_name}, regarding your upcoming driving lesson on ${liveBooking.booking_date} at ${liveBooking.booking_time}: `);
                        setShowManageModal(true);
                    }
          } else {
                    const [h, m] = time.split(':').map(Number);
                    const totalEndMins = (h * 60) + m + 60; // Adds 1 full hour
                    const endH = Math.floor(totalEndMins / 60).toString().padStart(2, '0');
                    const endM = (totalEndMins % 60).toString().padStart(2, '0');
                    setSelection({ date, startTime: time, endTime: `${endH}:${endM}` });
                    setSlotSelectedLearnerId(learners[0]?.id || '');
                    setShowSlotModal(true);
                }
            }} 
        />
    </div>

    {/* Right: Unified Live Messages & Notices Sidebar */}
    <div style={{ background: '#f8f9fa', border: '1px solid #cbd5e0', borderRadius: '12px', padding: '20px', flex: '1 1 340px', maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#0066cc', fontSize: '15px' }}>💬 Live Messages & Notices</h3>
            <span style={{ background: '#0066cc', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                {dispatchMessages.length + adminMessages.length} Total
            </span>
        </div>

        {/* Unified Message Stream */}
        {dispatchMessages.length === 0 && adminMessages.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontSize: '12px', border: '1px dashed #cbd5e0', borderRadius: '8px' }}>
                No incoming student messages or admin notices. You're all caught up!
            </div>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '450px', overflowY: 'auto' }}>
                
                {/* 1. Admin Notices Stream (Distinct Green Styling with Crown) */}
                {adminMessages.map(msg => (
                    <div key={`admin-${msg.id}`} style={{ background: '#f0fdf4', border: '1px solid #2f855a', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <strong style={{ fontSize: '12px', color: '#2f855a' }}>👑 School Administration</strong>
                            <span style={{ fontSize: '10px', color: '#64748b' }}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#166534', background: '#ffffff', padding: '8px', borderRadius: '6px', border: '1px solid #bbf7d0', wordBreak: 'break-word' }}>
                            {msg.message_text}
                        </p>
                        <button 
    onClick={() => handleDeleteAdminMessage(msg.id)}
    className="bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs px-3 py-1.5 rounded-lg transition"
>
    🗑️ Delete Notice
</button>
                    </div>
                ))}

{/* 2. Student Messages Stream (Threaded View) */}
                {Object.entries(
                    dispatchMessages.reduce((acc, msg) => {
                        const studentId = msg.learner_id;
                        if (!acc[studentId]) {
                            acc[studentId] = {
                                learnerName: msg.learner_name,
                                learnerId: studentId,
                                messages: []
                            };
                        }
                        acc[studentId].messages.push(msg);
                        return acc;
                    }, {})
                ).map(([studentId, thread]) => (
                    <div key={`thread-${studentId}`} style={{ background: '#ffffff', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                            <strong style={{ fontSize: '13px', color: '#1e293b' }}>🎓 {thread.learnerName}</strong>
                            <span style={{ fontSize: '10px', color: '#64748b' }}>{thread.messages.length} message(s)</span>
                        </div>
                        
                        {/* Chat Bubbles Stream */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
                            {thread.messages.map(m => {
                                const isInstructor = m.sender_role === 'instructor';
                                return (
                                    <div key={m.id} style={{ 
                                        background: isInstructor ? '#0066cc' : '#f8f9fa', 
                                        padding: '8px 10px', 
                                        borderRadius: '8px', 
                                        alignSelf: isInstructor ? 'flex-end' : 'flex-start',
                                        maxWidth: '85%',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{ fontSize: '9px', color: isInstructor ? '#bfdbfe' : '#64748b', marginBottom: '2px' }}>
                                            {isInstructor ? 'You' : thread.learnerName} • {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        <p style={{ fontSize: '12px', margin: 0, color: isInstructor ? '#ffffff' : '#334155', wordBreak: 'break-word' }}>
                                            {m.message_text}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Quick Reply & Delete Thread Actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input 
                                    type="text" 
                                    placeholder="Quick reply..." 
                                    value={replyTexts[thread.learnerId] || ''} 
                                    onChange={e => setReplyTexts({ ...replyTexts, [thread.learnerId]: e.target.value })}
                                    style={{ flex: 1, padding: '6px 10px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e0', background: '#fff', color: '#111', minWidth: '0' }}
                                />
                                <button 
                                    onClick={() => handleSendInstructorReply(thread.learnerId)}
                                    style={{ background: '#0066cc', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                    Reply
                                </button>
                            </div>
                            
                            <button 
                                onClick={async () => {
                                    if (window.confirm(`Are you sure you want to delete the entire conversation thread with ${thread.learnerName}? This is an ultimate wipe and cannot be undone.`)) {
                                        await fetch(`/api/messages/thread/${instructorId}/${thread.learnerId}`, { method: 'DELETE' });
                                        fetchDispatchMessages();
                                    }
                                }} 
                                style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center' }}
                            >
                                🗑️ Delete Thread
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}

        {/* 📢 Broadcast Announcement Box */}
        <div style={{ background: '#ffffff', border: '1px solid #cbd5e0', borderRadius: '10px', padding: '15px', marginTop: '15px', boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 6px 0', color: '#0066cc', fontSize: '14px' }}>📢 Broadcast Announcement</h3>
            <p style={{ fontSize: '11px', color: '#4a5568', marginBottom: '10px' }}>
                Instantly notify your entire active student roster.
            </p>
            <form onSubmit={handleSendBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <textarea
                    rows={2}
                    placeholder="e.g. Cancellation tomorrow at 2 PM!"
                    value={broadcastText}
                    onChange={e => setBroadcastText(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '12px', background: '#fff', color: '#111' }}
                />
                <button 
                    type="submit" 
                    disabled={isBroadcasting}
                    style={{ background: '#2f855a', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', alignSelf: 'flex-end' }}
                >
                    {isBroadcasting ? 'Broadcasting...' : '📢 Send Broadcast'}
                </button>
            </form>
        </div>

    </div>

</div>




<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '20px' }}>
                <div style={Styles.cardStyle}>
                    <h3 style={{ color: '#111111' }}>➕ Add New Student</h3>
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                            const response = await fetch('/api/learners', { 
                                method: 'POST', 
                                headers: { 'Content-Type': 'application/json' }, 
                                body: JSON.stringify({ 
                                    name: studentName, 
                                    email: studentEmail, 
                                    instructor_id: instructorId,
                                    training_category: trainingCategory 
                                }) 
                            });
                            if (response.ok) {
                                alert("🎉 Student added and secure activation email sent!");
                                setStudentName(''); 
                                setStudentEmail('');
                                fetchData(); 
                            } else {
                                const errData = await response.json();
                                alert(`❌ Error: ${errData.error || "Failed to add student"}`);
                            }
                        } catch (err) {
                            alert("❌ Server connection error.");
                        }
                    }} style={Styles.flexCol}>
                        <input placeholder="Full Name" style={Styles.inputStyle} value={studentName} onChange={e => setStudentName(e.target.value)} required />
                        <input type="email" placeholder="Email Address" style={Styles.inputStyle} value={studentEmail} onChange={e => setStudentEmail(e.target.value)} required />
                        
                        <label style={{fontSize: '12px', color: '#2d3748', marginTop: '5px', fontWeight: 'bold'}}>Training Vehicle Type</label>
                       <select 
    value={trainingCategory} 
    onChange={e => setTrainingCategory(e.target.value)} 
    style={Styles.selectStyle}
    required
>
    {(vehicleCategories && vehicleCategories.length > 0 ? vehicleCategories : ["Car (Manual)"]).map(cat => (
        <option key={cat} value={cat}>{cat}</option>
    ))}
</select>
                        
                        <button style={Styles.saveTemplateBtn}>Register Student & Send Setup Link</button>
                    </form>
                </div>

<div style={Styles.cardStyle}>
                    <h3 style={{ color: '#111111' }}>👥 Registered Students</h3>
                    {learners.map(l => {
                        const progressPercent = calculateProgress(l.skills);
                        
                        const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
                        const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
                        const doneHours = Math.round(doneMinutes / 60);

                        const todayStr = new Date().toISOString().slice(0, 10);
                        const studentBookings = instructorBookings.filter(b => 
                            (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
                            b.status === 'confirmed' &&
                            String(b.booking_date).slice(0, 10) >= todayStr
                        );
                        const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

                        return (
                            <div key={l.id} style={Styles.studentRow}>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ color: '#0066cc' }}>{l.name}</strong> <span style={{fontSize:'11px', color:'#4a5568'}}>({l.training_category || "Car (Manual)"})</span>
                                    
                                    <div style={{ fontSize: '11px', color: '#2f855a', fontWeight: 'bold', marginTop: '3px' }}>
                                        🕒 Done: {doneHours}h {bookedHours > 0 ? `| Booked: ${bookedHours}h` : ''}
                                    </div>

                                    <div style={{ marginTop: '5px', width: '160px' }}>
                                        <div style={Styles.miniBarContainer}><div style={{ ...Styles.miniBarFill, width: `${progressPercent}%`, backgroundColor: '#0066cc' }}></div></div>
                                    </div>
                                    <select value={l.status || 'Beginner'} onChange={e => handleStatusChange(l.id, e.target.value)} style={{ background: l.status === 'Passed' ? '#2f855a' : '#f8f9fa', color: l.status === 'Passed' ? '#ffffff' : '#111111', border: '1px solid #cbd5e0', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginTop: '8px', fontWeight: 'bold' }}>
                                        <option value="Beginner">Beginner</option>
                                        <option value="Intermediate">Intermediate</option>
                                        <option value="Test Ready">Test Ready</option>
                                        <option value="Passed">Passed</option>
                                    </select>
                                </div>

                                {/* ✉️ Direct Message Button */}
                                <button 
                                    onClick={() => {
                                        setTargetMessageStudent(l);
                                        setShowDirectMessageModal(true);
                                    }} 
                                    style={{ background: '#0066cc', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', marginRight: '5px' }}
                                >
                                    ✉️ Message
                                </button>

                                <button onClick={() => handleOpenTrackingPortal(l)} style={Styles.trackerBtn}>Track & Log</button>
                                <button onClick={() => handleRemoveStudent(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', marginLeft: '10px' }}>🗑️</button>
                            </div>
                        );
                    })}
                </div>
            </div>




 {/* --- MANAGE EXISTING BOOKING MODAL --- */}
            {showManageModal && selectedBooking && (
                <div style={Styles.modalOverlay}>
                    <div style={{ background: '#ffffff', padding: '25px', borderRadius: '15px', width: '400px', border: '1px solid #cbd5e0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', color: '#111111' }}>
                        <h3 style={{ color: '#0066cc', marginTop: 0 }}>🎓 Manage Lesson: {selectedBooking.learner_name}</h3>
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', margin: '15px 0', border: '1px solid #cbd5e0' }}>
                            <p>📅 <b>Current Date:</b> {new Date(selectedBooking.booking_date).toDateString()}</p>
                            <p>🕒 <b>Current Time:</b> {selectedBooking.booking_time}</p>
                            
                            <p>📍 <b>Pickup:</b> {selectedBooking.exact_pickup_address ? (
                                <span style={{ color: '#d69e2e', fontWeight: 'bold' }}>
                                    ({selectedBooking.pickup_type?.toUpperCase() || 'CUSTOM'}) {selectedBooking.exact_pickup_address}
                                </span>
                            ) : selectedBooking.student_default_address ? (
                                <span style={{ color: '#111111', fontWeight: 'bold' }}>
                                    ({selectedBooking.student_default_type?.toUpperCase() || 'HOME'}) {selectedBooking.student_default_address}
                                </span>
                            ) : (
                                <span style={{ color: '#4a5568', fontStyle: 'italic' }}>No address logged in profile</span>
                            )}</p>

                            {actionType === 'move' && (
                                <div style={{ marginTop: '15px', borderTop: '1px solid #cbd5e0', paddingTop: '10px' }}>
                                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Select New Date:</label>
                                    <input 
                                        type="date" 
                                        value={newBookingDate} 
                                        onChange={(e) => setNewBookingDate(e.target.value)} 
                                        style={{ display: 'block', margin: '5px 0 10px 0', width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
                                    />
                                    
                                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Select New Time:</label>
                                    <input 
                                        type="time" 
                                        value={newBookingTime} 
                                        onChange={(e) => setNewBookingTime(e.target.value)} 
                                        style={{ display: 'block', margin: '5px 0 10px 0', width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
                                    />

                                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Duration (Hours):</label>
                                    

<select 
    value={selectedBooking.duration || 1} 
    onChange={(e) => setSelectedBooking({ ...selectedBooking, duration: parseFloat(e.target.value) })}
    style={{ display: 'block', margin: '5px 0 10px 0', width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff' }}
>
       <option value={1}>1 Hour</option>
       <option value={2}>2 Hours</option>
        <option value={3}>3 Hours</option>
<option value={4}>4 Hours</option>
</select>

                                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Pickup Location:</label>
                                    <select 
                                        style={{ display: 'block', margin: '5px 0 10px 0', width: '100%', padding: '6px', border: '1px solid #cbd5e0', borderRadius: '4px', background: '#fff' }}
                                        value={selectedBooking.pickup_location || selectedBooking.primary_address}
                                        onChange={(e) => setSelectedBooking({ ...selectedBooking, pickup_location: e.target.value })}
                                    >
                                        <option value={selectedBooking.primary_address}>
                                            🏠 Home: {selectedBooking.primary_address}
                                        </option>
                                        {selectedBooking.secondary_address && (
                                            <option value={selectedBooking.secondary_address}>
                                                💼 {selectedBooking.secondary_label}: {selectedBooking.secondary_address}
                                            </option>
                                        )}
                                    </select>

                                    <button onClick={handleMoveBooking} disabled={isProcessing} style={{ ...Styles.saveTemplateBtn, background: '#0066cc', marginRight: '5px', border: 'none' }}>
                                        Save New Time
                                    </button>
                                    <button onClick={() => setActionType('view')} style={{ ...Styles.saveTemplateBtn, background: '#4a5568', border: 'none' }}>Cancel</button>
                                </div>
                            )}

                            {actionType === 'message' && (
                                <div style={{ marginTop: '15px', borderTop: '1px solid #cbd5e0', paddingTop: '10px' }}>
                                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>Draft Message to Student:</label>
                                    <textarea 
                                        value={messageText} 
                                        onChange={(e) => setMessageText(e.target.value)}
                                        rows={4}
                                        style={{ width: '100%', padding: '8px', marginTop: '5px', borderRadius: '4px', background: '#ffffff', color: '#111111', border: '1px solid #cbd5e0' }}
                                    />
                                    <button onClick={handleSendMessage} style={{ ...Styles.saveTemplateBtn, background: '#2f855a', marginRight: '5px', marginTop: '8px', border: 'none' }}>
                                        Send Message
                                    </button>
                                    <button onClick={() => setActionType('view')} style={{ ...Styles.saveTemplateBtn, background: '#4a5568', marginTop: '8px', border: 'none' }}>Cancel</button>
                                </div>
                            )}
                        </div>

                        {actionType === 'view' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <button onClick={() => setActionType('move')} style={{ ...Styles.saveTemplateBtn, background: '#d69e2e', border: 'none' }}>
                                    🔄 Reschedule / Move Lesson
                                </button>
                                <button onClick={() => setActionType('message')} style={{ ...Styles.saveTemplateBtn, background: '#0066cc', border: 'none' }}>
                                    ✉️ Message Student
                                </button>
                                <button onClick={() => handleCancelBooking(selectedBooking.id)} style={{ ...Styles.saveTemplateBtn, background: '#e53e3e', border: 'none' }}>
                                    ❌ Cancel Booking
                                </button>
                                <button onClick={() => setShowManageModal(false)} style={{ ...Styles.saveTemplateBtn, background: '#e2e8f0', color: '#111111', border: 'none' }}>
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

    {selectedStudent && showProgressModal && (
    <div style={Styles.modalOverlay}>
        <div ref={modalContentRef} style={{...Styles.largeModal, width: '1200px', height: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', color: '#111111', border: '1px solid #cbd5e0', overflowY: 'auto'}}>             
                        {/* --- MODAL HEADER & GLOBAL ACTIONS --- */}
                        <div style={{...Styles.modalHeader, borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <div>
                                <h2 style={{margin: 0, color: '#111111', fontSize: '20px'}}>
                                    🎯 {selectedStudent.name} Progress & Reporting Hub ({selectedStudent.training_category || "Car (Manual)"})
                                </h2>
                                <span style={{fontSize: '12px', color: '#4a5568'}}>Structured lesson audit and student progression record.</span>
                            </div>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <button 
                                    onClick={handleGeneratePrintReport} 
                                    style={{...Styles.navBtn, background: '#f8f9fa', border: '1px solid #cbd5e0', color: '#111111', fontWeight: 'bold', cursor: 'pointer'}}
                                >
                                    🖨️ Print Student Report
                                </button>

                                {selectedStudent.isReadOnlyAudit ? (
                                    <button 
                                        onClick={() => setSelectedStudent(prev => ({ ...prev, isReadOnlyAudit: false }))} 
                                        style={{...Styles.saveTemplateBtn, background: '#d69e2e', padding: '8px 16px', fontSize: '14px', border: 'none', color: '#ffffff', cursor: 'pointer'}}
                                    >
                                        🔓 Unlock to Edit
                                    </button>
                                ) : (
                                    <button 
                                        onClick={handleCommitCompleteLessonReport} 
                                        style={{...Styles.saveTemplateBtn, background: '#0066cc', padding: '8px 16px', fontSize: '14px', border: 'none', color: '#ffffff', cursor: 'pointer'}}
                                    >
                                        {isSaving ? 'Saving...' : '💾 Save All Changes'}
                                    </button>
                                )}

                                <button onClick={() => { setShowProgressModal(false); fetchData(); }} style={{...Styles.closeBtn, background:'#e53e3e', color:'white', padding:'8px 12px', borderRadius:'6px', border: 'none', cursor: 'pointer'}}>✕ Close</button>
                            </div>
                        </div>

                        {/* --- SINGLE STREAMLINED 4-TAB NAVIGATION BAR --- */}
 <div style={{display: 'flex', gap: '10px', padding: '12px 20px', background: '#f8f9fa', borderBottom: '1px solid #cbd5e0', flexWrap: 'wrap'}}>
    <button 
        onClick={() => setModalActiveTab('lesson')}
        style={{padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid #cbd5e0', cursor: 'pointer', background: modalActiveTab === 'lesson' ? '#0066cc' : '#ffffff', color: modalActiveTab === 'lesson' ? '#ffffff' : '#111111'}}
    >
        📝 1. Lesson Activity & Notes
    </button>
    <button 
        onClick={() => setModalActiveTab('matrix')}
        style={{padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid #cbd5e0', cursor: 'pointer', background: modalActiveTab === 'matrix' ? '#0066cc' : '#ffffff', color: modalActiveTab === 'matrix' ? '#ffffff' : '#111111'}}
    >
        🚗 2. DVSA Syllabus Matrix
    </button>
    <button 
        onClick={() => setModalActiveTab('readiness')}
        style={{padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid #cbd5e0', cursor: 'pointer', background: modalActiveTab === 'readiness' ? '#0066cc' : '#ffffff', color: modalActiveTab === 'readiness' ? '#ffffff' : '#111111'}}
    >
        📊 3. Progress & History Summary
    </button>
    <button 
        onClick={() => setModalActiveTab('profile')}
        style={{padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', border: '1px solid #cbd5e0', cursor: 'pointer', background: modalActiveTab === 'profile' ? '#0066cc' : '#ffffff', color: modalActiveTab === 'profile' ? '#ffffff' : '#111111'}}
    >
        👤 4. Student Profile & Details
    </button>
</div>



{/* --- TAB CONTENT CONTAINER --- */}
<div style={{flex: 1, padding: '20px', background: '#ffffff'}}>

     
{/* TAB 1: LESSON ACTIVITY, NOTES & TEST READINESS */}
{modalActiveTab === 'lesson' && (
    <div style={{display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '900px', margin: '0 auto'}}>
        
        {/* Date & Duration Banner */}
        <div style={{background: '#f8f9fa', padding: '15px 20px', borderRadius: '10px', border: '1px solid #cbd5e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div>
                <span style={{fontSize: '11px', color: '#4a5568', fontWeight: 'bold', display: 'block'}}>BOOKED LESSON DATE:</span>
                <span style={{color: '#2f855a', fontWeight: 'bold', fontSize: '14px'}}>📅 {logDate} (Locked to Calendar)</span>
            </div>
            <div>
                <span style={{fontSize: '11px', color: '#4a5568', fontWeight: 'bold', display: 'block'}}>SESSION DURATION:</span>
                <span style={{color: '#0066cc', fontWeight: 'bold', fontSize: '14px'}}>🕒 {logDuration / 60} Hour(s)</span>
            </div>
        </div>

        {/* Overall Session Grade */}
        <div style={{background: '#fffdf4', padding: '20px', borderRadius: '12px', border: '2px solid #d69e2e'}}>
            <h4 style={{margin:'0 0 5px 0', color:'#b7791f'}}>⭐ Overall Session Grade (Required)</h4>
            <p style={{fontSize: '11px', color: '#744210', marginBottom: '10px'}}>Select student grade performance for this lesson:</p>
            <select 
                value={logProgressScore} 
                onChange={e => setLogProgressScore(parseInt(e.target.value, 10))} 
                style={{...Styles.selectStyle, padding: '10px', width: '100%', fontWeight: 'bold', background: '#ffffff', border: '1px solid #d69e2e'}} 
                disabled={selectedStudent.isReadOnlyAudit}
            >
                {Object.entries(scoringGuide).map(([val, lbl]) => <option key={val} value={val}>{val} - {lbl}</option>)}
            </select>
        </div>

        {/* Session Notes & Quick Cues */}
        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
            <h4 style={{margin:'0 0 8px 0', color:'#0066cc'}}>📝 Lesson Summary & Quick Cues</h4>
            <p style={{fontSize: '12px', color: '#4a5568', marginBottom: '12px'}}>Tap quick-cues below to instantly populate feedback fields:</p>
            
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px'}}>
                {summaryCues.map((cue, idx) => (
                    <button 
                        key={idx}
                        type="button" 
                        onClick={() => setCustomLogNotes(prev => prev ? prev + " " + cue : cue)} 
                        style={{fontSize: '11px', padding: '6px 12px', background: '#ffffff', border: '1px solid #0066cc', color: '#0066cc', borderRadius: '15px', cursor: 'pointer', fontWeight: '600'}}
                    >
                        {cue}
                    </button>
                ))}
            </div>
            <label style={{fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px'}}>Session Notes / Summary:</label>
            <textarea 
                style={{...Styles.logInput, height: '70px', width: '100%', background: selectedStudent.isReadOnlyAudit ? '#e2e8f0' : '#ffffff', color: '#111111', border: '1px solid #cbd5e0', padding: '10px'}} 
                value={customLogNotes} 
                disabled={selectedStudent.isReadOnlyAudit}
                onChange={e => setCustomLogNotes(e.target.value)} 
                placeholder="Summarise lesson performance..." 
            />
        </div>

        {/* Next Steps & Objectives */}
        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
            <h4 style={{margin:'0 0 8px 0', color:'#2f855a'}}>➡️ Next Steps & Target Objectives</h4>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px'}}>
                {nextStepsCues.map((cue, idx) => (
                    <button 
                        key={idx}
                        type="button" 
                        onClick={() => setNextStepsPlan(prev => prev ? prev + " " + cue : cue)} 
                        style={{fontSize: '11px', padding: '6px 12px', background: '#ffffff', border: '1px solid #2f855a', color: '#2f855a', borderRadius: '15px', cursor: 'pointer', fontWeight: '600'}}
                    >
                        {cue}
                    </button>
                ))}
            </div>
            <label style={{fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px'}}>Next Steps Plan:</label>
            <textarea 
                style={{...Styles.logInput, height: '70px', width: '100%', background: selectedStudent.isReadOnlyAudit ? '#e2e8f0' : '#ffffff', color: '#111111', border: '1px solid #cbd5e0', padding: '10px'}} 
                value={nextStepsPlan} 
                disabled={selectedStudent.isReadOnlyAudit}
                onChange={e => setNextStepsPlan(e.target.value)} 
                placeholder="What to target next..." 
            />
        </div>

        {/* Test Readiness Checklist (Moved Here) */}
        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
            <h4 style={{margin:'0 0 5px 0', color:'#2f855a'}}>📋 Test Readiness Checklist</h4>
            <p style={{fontSize: '12px', color: '#4a5568', marginBottom: '15px'}}>Click requirements to toggle progress status.</p>
            <div style={{display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap:'10px'}}>
                {MASTER_READINESS_CHECKLIST.map(item => {
                    const ready = readinessChecklist.includes(item);
                    return (
                        <div 
                            key={item} 
                            onClick={() => { if (!selectedStudent.isReadOnlyAudit) toggleChecklistItem(item); }} 
                            style={{display:'flex', justifyContent:'space-between', padding:'10px 14px', background:'#ffffff', borderRadius:'8px', cursor: selectedStudent.isReadOnlyAudit ? 'default' : 'pointer', border: ready ? '1px solid #2f855a' : '1px solid #cbd5e0', alignItems: 'center'}}
                        >
                            <span style={{fontSize:'12px', color: '#111111', fontWeight: '500'}}>{item}</span>
                            <span>{ready ? '✅' : '⏳'}</span>
                        </div>
                    )
                })}
            </div>
        </div>

    </div>
)}

{/* TAB 2: DVSA SYLLABUS MATRIX (3-Column Grouped Layout) */}
{modalActiveTab === 'matrix' && (
    <div style={{display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1200px', margin: '0 auto'}}>
        
        <div style={{background: '#f8f9fa', padding: '15px 20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
            <h4 style={{margin: '0 0 5px 0', color: '#0066cc'}}>📁 Core Competencies & Syllabus Matrix</h4>
            <p style={{fontSize: '12px', color: '#4a5568', margin: 0}}>Rate student competency across all DVSA framework levels (1 to 5) grouped clearly across categories.</p>
        </div>

        {/* 3-Column Grid Layout for Categories */}
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px'}}>
            {Object.entries(DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent?.training_category || "Car (Manual)"] || {}).map(([category, skills]) => (
                <div key={category} style={{background: '#ffffff', padding: '16px', borderRadius: '10px', border: '1px solid #cbd5e0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box'}}>
                    <h5 style={{margin: '0 0 12px 0', color: '#1e293b', fontSize: '14px', borderBottom: '2px solid #e2e8f0', paddingBottom: '6px', fontWeight: 'bold'}}>📁 {category}</h5>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        {skills.map(skill => {
                            const currentScore = selectedStudent?.skills?.[skill] || 1;
                            return (
                                <div key={skill} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8f9fa', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0'}}>
                                    <span style={{fontSize: '12px', color: '#111111', fontWeight: '500', maxWidth: '60%'}}>{skill}</span>
                                    <div style={{display: 'flex', gap: '4px'}}>
                                        {[1,2,3,4,5].map(idx => {
                                            const active = idx <= currentScore;
                                            let c = '#e2e8f0';
                                            let textColor = '#111111';
                                            if (active) {
                                                c = currentScore === 5 ? '#2f855a' : currentScore >= 3 ? '#0066cc' : '#e53e3e';
                                                textColor = '#ffffff';
                                            }
                                            return (
                                                <button 
                                                    key={idx} 
                                                    disabled={selectedStudent.isReadOnlyAudit}
                                                    onClick={() => {
                                                        if (selectedStudent.isReadOnlyAudit) return;
                                                        const updated = { ...selectedStudent.skills, [skill]: idx };
                                                        setSelectedStudent(prev => ({ ...prev, skills: updated }));
                                                    }}
                                                    style={{width:'26px', height:'26px', border:'none', borderRadius:'6px', background: c, color: textColor, fontSize:'11px', fontWeight: 'bold', cursor: selectedStudent.isReadOnlyAudit ? 'not-allowed' : 'pointer'}}
                                                >
                                                    {idx}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            ))}
        </div>

    </div>
)}
                            {/* TAB 3: READINESS GATE, VISUAL SUMMARY & ANALYTICS */}
                            {modalActiveTab === 'readiness' && (() => {
                                const syllabusPercent = calculateProgress(selectedStudent.skills);
                                const totalChecklist = MASTER_READINESS_CHECKLIST.length;
                                const achievedChecklist = readinessChecklist.length;
                                const checklistPercent = Math.round((achievedChecklist / totalChecklist) * 100) || 0;
                                const overallReadiness = Math.round((syllabusPercent + checklistPercent) / 2);
                                
                                const loggedMinutes = historicalLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
                                const loggedHours = loggedMinutes / 60;
                                const priorHours = Number(selectedStudent.prior_hours_experience) || 0;
                                const totalHoursDriven = (priorHours + loggedHours).toFixed(1);

                                let statusBadgeColor = '#e53e3e';
                                let statusText = 'Foundation Stage (Needs Development)';
                                if (overallReadiness >= 80) {
                                    statusBadgeColor = '#2f855a';
                                    statusText = '🌟 Test Ready / Exam Standard';
                                } else if (overallReadiness >= 50) {
                                    statusBadgeColor = '#0066cc';
                                    statusText = '📈 Intermediate Progress';
                                }

                                return (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1000px', margin: '0 auto'}}>
                                        <div style={{background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'}}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
                                                <div>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                                                        <span style={{fontSize: '16px'}}>🕒</span>
                                                        <h4 style={{margin: 0, color: '#0066cc', fontSize: '15px'}}>Total Training Volume Completed</h4>
                                                    </div>
                                                    <p style={{margin: 0, fontSize: '13px', color: '#4a5568'}}>
                                                        <b>{totalHoursDriven} Hours Total</b> ({priorHours} hrs prior experience + {loggedHours.toFixed(1)} hrs recorded across {historicalLogs.length} sessions).
                                                    </p>
                                                </div>

                                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                    <span style={{fontSize: '12px', fontWeight: 'bold', color: '#4a5568'}}>Status:</span>
                                                    <select 
                                                        value={selectedStudent.status || 'Beginner'} 
                                                        onChange={async (e) => {
                                                            const newStatus = e.target.value;
                                                            setSelectedStudent(prev => ({ ...prev, status: newStatus }));
                                                            setLearners(prev => prev.map(l => parseInt(l.id, 10) === parseInt(selectedStudent.id, 10) ? { ...l, status: newStatus } : l));
                                                            await fetch(`/api/learners/${selectedStudent.id}/status`, { 
                                                                method: 'PATCH', 
                                                                headers: { 'Content-Type': 'application/json' }, 
                                                                body: JSON.stringify({ status: newStatus }) 
                                                            });
                                                        }} 
                                                        style={{ background: selectedStudent.status === 'Passed' ? '#2f855a' : '#0066cc', color: '#ffffff', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                                                    >
                                                        <option value="Beginner">Beginner</option>
                                                        <option value="Intermediate">Intermediate</option>
                                                        <option value="Test Ready">Test Ready</option>
                                                        <option value="Passed">Passed 🏆</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                                                <h4 style={{margin: 0, color: '#0066cc'}}>📊 Visual Test Readiness & Exam Proximity</h4>
                                                <span style={{padding: '4px 10px', background: statusBadgeColor, color: '#ffffff', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold'}}>{statusText}</span>
                                            </div>
                                            <p style={{fontSize: '12px', color: '#4a5568', marginBottom: '15px'}}>Combined metric tracking core syllabus mastery and mandatory test-readiness milestones.</p>

                                            <div style={{marginBottom: '12px'}}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px'}}>
                                                    <span>Syllabus Competency Mastery</span>
                                                    <span>{syllabusPercent}%</span>
                                                </div>
                                                <div style={{width: '100%', height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden', border: '1px solid #cbd5e0'}}>
                                                    <div style={{height: '100%', width: `${syllabusPercent}%`, background: syllabusPercent >= 75 ? '#2f855a' : '#0066cc', transition: 'width 0.4s ease'}}></div>
                                                </div>
                                            </div>

                                            <div style={{marginBottom: '8px'}}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px'}}>
                                                    <span>Test-Readiness Gate ({achievedChecklist} / {totalChecklist} Requisites Met)</span>
                                                    <span>{checklistPercent}%</span>
                                                </div>
                                                <div style={{width: '100%', height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden', border: '1px solid #cbd5e0'}}>
                                                    <div style={{height: '100%', width: `${checklistPercent}%`, background: checklistPercent >= 80 ? '#2f855a' : '#d69e2e', transition: 'width 0.4s ease'}}></div>
                                                </div>
                                            </div>
                                        </div>

                                        <StudentProgressReport student={selectedStudent} />

                                        <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0', marginTop: '10px'}}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                                                <h4 style={{margin: 0, color: '#0066cc'}}>📜 Historical Lesson Audit Feed</h4>
                                                <span style={{fontSize: '11px', color: '#64748b'}}>Past session notes and performance grades</span>
                                            </div>

                                            {historicalLogs.length === 0 ? (
                                                <div style={{padding: '20px', textAlign: 'center', color: '#4a5568', border: '1px dashed #cbd5e0', borderRadius: '8px'}}>No historical logs recorded for this student yet.</div>
                                            ) : (
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '380px', overflowY: 'auto'}}>
                                                    {historicalLogs.map((log, index) => {
                                                        const formattedDate = log.lesson_date ? new Date(log.lesson_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Recent Session';
                                                        const durationMins = log.duration_minutes || (log.duration ? log.duration * 60 : 60);
                                                        const hoursDisplay = durationMins >= 60 ? `${durationMins / 60}h` : `${durationMins}m`;

                                                        return (
                                                            <div 
                                                                key={log.id || index} 
                                                                style={{
                                                                    background: '#ffffff', 
                                                                    border: '1px solid #cbd5e0', 
                                                                    borderRadius: '8px', 
                                                                    padding: '14px',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                                                }}
                                                            >
                                                                <div style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '10px', alignItems: 'center'}}>
                                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                        <span style={{fontSize: '12px', fontWeight: 'bold', color: '#0066cc'}}>📅 {formattedDate}</span>
                                                                        <span style={{fontSize: '11px', color: '#4a5568', background: '#f8f9fa', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0'}}>🕒 {hoursDisplay}</span>
                                                                    </div>
                                                                </div>

                                                                <div style={{background: '#fffdf4', padding: '10px', borderRadius: '6px', border: '1px solid #d69e2e', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                                    <div>
                                                                        <span style={{fontSize: '11px', fontWeight: 'bold', color: '#b7791f', display: 'block'}}>⭐ Overall Session Grade:</span>
                                                                        <span style={{fontSize: '10px', color: '#744210'}}>Student performance rating for this session</span>
                                                                    </div>
                                                                    <span style={{fontSize: '12px', padding: '4px 10px', background: '#d69e2e', color: '#ffffff', borderRadius: '6px', fontWeight: 'bold'}}>
                                                                        Grade: {log.progress_score}/5
                                                                    </span>
                                                                </div>

                                                                <div style={{marginBottom: '8px'}}>
                                                                    <span style={{fontSize: '10px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase'}}>💬 Lesson Summary:</span>
                                                                    <p style={{background: '#f8f9fa', padding: '8px', borderRadius: '4px', margin: 0, color: '#111111', fontSize: '12px', border: '1px solid #edf2f7'}}>
                                                                        {log.custom_notes || log.notes || 'No summary notes entered for this session.'}
                                                                    </p>
                                                                </div>

                                                                <div>
                                                                    <span style={{fontSize: '10px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase'}}>➡️ Target Objectives / Next Steps:</span>
                                                                    <div style={{background: '#f0fdf4', padding: '8px', borderRadius: '4px', color: '#166534', fontSize: '12px', border: '1px solid #bbf7d0'}}>
                                                                        {log.next_steps || log.action_plan || 'No specific next steps recorded.'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

{/* TAB 4: EDITABLE STUDENT PROFILE & MERGED ONBOARDING DETAILS */}
                            {modalActiveTab === 'profile' && (
                                <div style={{display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '850px', margin: '0 auto'}}>
                                    
                                    {/* Header & Edit/Save Controller */}
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8f9fa', padding: '16px 20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
                                        <div>
                                            <h4 style={{margin: '0 0 2px 0', color: '#0066cc'}}>👤 Student Profile & Registration</h4>
                                            <span style={{fontSize: '11px', color: '#4a5568'}}>Manage contact info, pickup locations, and milestone dates.</span>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={async () => {
                                                if (isEditingProfile) {
                                                    try {
                                                        const response = await fetch(`/api/learners/${selectedStudent.id}/onboarding`, {
                                                            method: 'PATCH',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({
                                                                name: selectedStudent.name,
                                                                email: selectedStudent.email,
                                                                license_number: selectedStudent.license_number,
                                                                date_of_birth: selectedStudent.date_of_birth,
                                                                phone_number: selectedStudent.phone_number || selectedStudent.phone,
                                                                default_pickup_address: selectedStudent.default_pickup_address || selectedStudent.address,
                                                                secondary_pickup: selectedStudent.secondary_pickup,
                                                                emergency_contact: selectedStudent.emergency_contact,
                                                                prior_hours_experience: selectedStudent.prior_hours_experience,
                                                                theory_passed_date: selectedStudent.theory_passed_date,
                                                                practical_test_date: selectedStudent.practical_test_date
                                                            })
                                                        });
                                                        if (response.ok) {
                                                            alert("🎉 Profile updated successfully!");
                                                            setIsEditingProfile(false);
                                                            fetchData();
                                                        } else {
                                                            const errData = await response.json();
                                                            alert(`❌ Error: ${errData.error || 'Failed to update'}`);
                                                        }
                                                    } catch (err) {
                                                        console.error("Profile update error:", err);
                                                    }
                                                } else {
                                                    setIsEditingProfile(true);
                                                }
                                            }}
                                            style={{
                                                background: isEditingProfile ? '#2f855a' : '#0066cc',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '6px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            {isEditingProfile ? '💾 Save Profile Changes' : '✏️ Edit Profile'}
                                        </button>
                                    </div>

                                    {/* Registration & Basic Info */}
                                    <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px'}}>
                                            
                                            {/* Full Name */}
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>FULL NAME:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.name || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, name: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600', fontSize: '13px'}}>{selectedStudent.name || 'Not provided'}</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>TRAINING CATEGORY:</span>
                                                <span style={{color: '#2f855a', fontWeight: '600', fontSize: '13px'}}>{selectedStudent.training_category || "Car (Manual)"}</span>
                                            </div>

                                            {/* Email Address */}
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>EMAIL ADDRESS:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="email"
                                                        value={selectedStudent.email || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, email: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.email || 'Not provided'}</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>PHONE NUMBER:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.phone_number || selectedStudent.phone || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, phone_number: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.phone_number || selectedStudent.phone || 'Not provided'}</span>
                                                )}
                                            </div>

                                            {/* Driving License Number */}
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>DRIVING LICENSE NUMBER:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.license_number || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, license_number: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#0066cc', fontWeight: '600'}}>{selectedStudent.license_number || 'Pending / Not Entered'}</span>
                                                )}
                                            </div>

                                            {/* 🎂 Read-Only Date of Birth Box */}
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>DATE OF BIRTH:</span>
                                                <span style={{color: '#111111', fontWeight: '600', fontSize: '13px'}}>
                                                    {selectedStudent.date_of_birth ? new Date(selectedStudent.date_of_birth).toLocaleDateString('en-GB') : 'Not provided'}
                                                </span>
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>REGISTRATION DATE:</span>
                                                <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.created_at ? new Date(selectedStudent.created_at).toLocaleDateString('en-GB') : 'Standard Record'}</span>
                                            </div>

                                        </div>
                                    </div>

                                    {/* Pickup Locations & Emergency Contacts */}
                                    <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
                                        <h4 style={{margin: '0 0 15px 0', color: '#0066cc'}}>📍 Locations & Emergency Contacts</h4>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '12px'}}>
                                            
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>PRIMARY PICKUP ({selectedStudent.default_pickup_type || 'home'}):</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.default_pickup_address || selectedStudent.address || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, default_pickup_address: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.default_pickup_address || selectedStudent.address || 'Not set'}</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>SECONDARY PICKUP ({selectedStudent.secondary_type || 'N/A'}):</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.secondary_pickup || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, secondary_pickup: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.secondary_pickup || 'None registered'}</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', gridColumn: 'span 2'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>🆘 EMERGENCY CONTACT:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="text"
                                                        value={selectedStudent.emergency_contact || ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, emergency_contact: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.emergency_contact || 'Not provided'}</span>
                                                )}
                                            </div>

                                        </div>
                                    </div>

                                    {/* Driving Milestones & Test Dates */}
                                    <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0'}}>
                                        <h4 style={{margin: '0 0 15px 0', color: '#0066cc'}}>🏁 Driving Milestones & Test Dates</h4>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '12px'}}>
                                            
                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>PRIOR EXPERIENCE:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="number"
                                                        value={selectedStudent.prior_hours_experience || 0}
                                                        onChange={e => setSelectedStudent({...selectedStudent, prior_hours_experience: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#111111', fontWeight: '600'}}>{selectedStudent.prior_hours_experience || 0} Hours</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>THEORY PASS DATE:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="date"
                                                        value={(() => {
                                                            if (!selectedStudent.theory_passed_date) return '';
                                                            const str = String(selectedStudent.theory_passed_date);
                                                            if (str.length >= 10 && str.includes('-')) return str.slice(0, 10);
                                                            const d = new Date(selectedStudent.theory_passed_date);
                                                            if (isNaN(d.getTime())) return '';
                                                            const year = d.getUTCFullYear();
                                                            const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                                                            const day = String(d.getUTCDate()).padStart(2, '0');
                                                            return `${year}-${month}-${day}`;
                                                        })()}
                                                        onChange={e => setSelectedStudent({...selectedStudent, theory_passed_date: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#2f855a', fontWeight: '600'}}>{selectedStudent.theory_passed_date ? new Date(selectedStudent.theory_passed_date).toLocaleDateString('en-GB') : 'Not passed yet'}</span>
                                                )}
                                            </div>

                                            <div style={{background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                                                <span style={{color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px'}}>PRACTICAL TEST DATE:</span>
                                                {isEditingProfile ? (
                                                    <input 
                                                        type="date"
                                                        value={selectedStudent.practical_test_date ? selectedStudent.practical_test_date.slice(0, 10) : ''}
                                                        onChange={e => setSelectedStudent({...selectedStudent, practical_test_date: e.target.value})}
                                                        style={{width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '12px', marginTop: '2px'}}
                                                    />
                                                ) : (
                                                    <span style={{color: '#0066cc', fontWeight: '600'}}>{selectedStudent.practical_test_date ? new Date(selectedStudent.practical_test_date).toLocaleDateString('en-GB') : 'Not booked yet'}</span>
                                                )}
                                            </div>

                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}



            {showSlotModal && selection && (
                <div style={Styles.modalOverlay}>
                    <div style={{ background: '#ffffff', padding: '25px', borderRadius: '15px', width: '420px', border: '1px solid #cbd5e0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', color: '#111111' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ color: '#111111', margin: 0, fontSize: '16px' }}>
                                Manage Slot: {selection.date.toLocaleDateString()} ({selection.startTime})
                            </h3>
                            <button 
                                onClick={() => setShowSlotModal(false)} 
                                style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                                What would you like to do?
                            </label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setSlotIntent('book')}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: slotIntent === 'book' ? '2px solid #0066cc' : '1px solid #cbd5e0',
                                        background: slotIntent === 'book' ? '#ebf8ff' : '#f8f9fa',
                                        color: slotIntent === 'book' ? '#0066cc' : '#111',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    🔘 Book Lesson
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSlotIntent('block')}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: slotIntent === 'block' ? '2px solid #e53e3e' : '1px solid #cbd5e0',
                                        background: slotIntent === 'block' ? '#fff5f5' : '#f8f9fa',
                                        color: slotIntent === 'block' ? '#e53e3e' : '#111',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    ⚪ Block Time-Off
                                </button>
                            </div>
                        </div>

                      

<div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #cbd5e0' }}>
    <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
        🕒 {slotIntent === 'book' ? 'Lesson' : 'Block'} Duration:
    </label>
<select 
    value={(() => {
        const [startH, startM] = selection.startTime.split(':').map(Number);
        const [endH, endM] = selection.endTime.split(':').map(Number);
        return ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
    })()}
    onChange={e => {
        const durationValue = parseFloat(e.target.value);
        const [startH, startM] = selection.startTime.split(':').map(Number);
        const startTotalMins = (startH * 60) + startM + Math.round(durationValue * 60);
        const endH = Math.floor(startTotalMins / 60).toString().padStart(2, '0');
        const endM = (startTotalMins % 60).toString().padStart(2, '0');
        setSelection(prev => ({ ...prev, endTime: `${endH}:${endM}` }));
        if (slotIntent === 'book') {
            setSlotDurationHours(durationValue);
        }
    }}
    style={{ ...Styles.selectStyle, width: '100%', marginBottom: '6px' }}
>
    {slotIntent === 'book' ? (
        // 🚗 Full hours only for student bookings
        <>
            <option value={1}>1 Hour</option>
            <option value={2}>2 Hours</option>
            <option value={3}>3 Hours</option>
            <option value={4}>4 Hours</option>
        </>
    ) : (
        // 🛑 Fine-grained increments for instructor time-off blocks
        <>
            <option value={0.25}>15 Minutes</option>
            <option value={0.5}>30 Minutes</option>
            <option value={0.75}>45 Minutes</option>
            <option value={1}>1 Hour</option>
            <option value={1.5}>1 Hour 30 Minutes</option>
            <option value={2}>2 Hours</option>
        </>
    )}
</select>
    <p style={{ fontSize: '11px', color: '#4a5568', margin: 0 }}>
        <b>Time Range:</b> {selection.startTime} to {selection.endTime}
    </p>
</div>




                        {slotIntent === 'book' ? (
                            <>
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>🎓 Direct Book Student:</label>
                                    <select 
                                        value={slotSelectedLearnerId} 
                                        onChange={e => {
                                            setSlotSelectedLearnerId(e.target.value);
                                            const student = learners.find(l => parseInt(l.id, 10) === parseInt(e.target.value, 10));
                                            if (student) {
                                                setSlotPickupType('primary');
                                                setSlotCustomPickup(student.default_pickup_address || student.address || '');
                                            }
                                        }}
                                        style={{ ...Styles.selectStyle, width: '100%' }}
                                    >
                                        <option value="">-- Select Student --</option>
                                        {learners.map(l => (
                                            <option key={l.id} value={l.id}>{l.name} ({l.training_category || "Car"})</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>📍 Pickup Location Choice:</label>
                                    <select 
                                        value={slotPickupType} 
                                        onChange={e => {
                                            setSlotPickupType(e.target.value);
                                            const student = learners.find(l => parseInt(l.id, 10) === parseInt(slotSelectedLearnerId, 10));
                                            if (student) {
                                                if (e.target.value === 'primary') {
                                                    setSlotCustomPickup(student.default_pickup_address || student.address || '');
                                                } else if (e.target.value === 'secondary') {
                                                    setSlotCustomPickup(student.secondary_pickup || '');
                                                }
                                            }
                                        }}
                                        style={{ ...Styles.selectStyle, width: '100%', marginBottom: '8px' }}
                                    >
                                        <option value="primary">Primary Address (Home)</option>
                                        <option value="secondary">Secondary Address (Work/Uni)</option>
                                        <option value="custom">Custom / Other Address</option>
                                    </select>
                                    <input 
                                        type="text"
                                        value={slotCustomPickup}
                                        onChange={e => setSlotCustomPickup(e.target.value)}
                                        placeholder="Confirm or type exact address..."
                                        style={{ ...Styles.inputStyle, width: '100%', fontSize: '12px' }}
                                    />
                                </div>

                                <button onClick={handleDirectInstructorBooking} style={{ ...Styles.saveTemplateBtn, width: '100%', background: '#0066cc', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                                    Confirm & Secure Booking ✅
                                </button>
                            </>
                        ) : (
                            <>
                                <p style={{ fontSize: '12px', color: '#4a5568', marginBottom: '15px' }}>
                                    Blocking this time range will prevent students from booking online.
                                </p>
                                <button onClick={() => handleSaveSlot('blocked')} style={{ ...Styles.saveTemplateBtn, width: '100%', background: '#e53e3e', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                                    Apply Time-Off Lock 🛑
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

   {showSettingsDrawer && (
                <SettingsDrawer 
                    setShowSettingsDrawer={setShowSettingsDrawer}
                    instructorEmail={instructorEmail}
                    instructorPhone={instructorPhone}
                    setInstructorPhone={setInstructorPhone}
                    hqPostcode={hqPostcode}
                    setHqPostcode={setHqPostcode}
                    findNearbyAreas={findNearbyAreas}
                    nearbyAreas={nearbyAreas}
                    selectedAreas={selectedAreas}
                    setSelectedAreas={setSelectedAreas}
                    travelRadius={travelRadius}
                    setTravelRadius={setTravelRadius}
                    vehicleCategories={vehicleCategories}
                    setVehicleCategories={setVehicleCategories}
                    triggerMasterConfigurationSave={triggerMasterConfigurationSave}
                    instructorDob={instructorDob}
                    instructorBadgeNumber={instructorBadgeNumber}
                    instructorId={instructorId}
instructorName={instructorName}           
                    postcodeLatitude={postcodeLatitude}       
                    postcodeLongitude={postcodeLongitude}
                />
            )}

            {/* 📖 Instructor Help Modal */}
            <InstructorHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

            {/* ✉️ Direct Message Modal */}
            {showDirectMessageModal && targetMessageStudent && (
                <div style={Styles.modalOverlay}>
                    <div style={{ background: '#ffffff', padding: '25px', borderRadius: '15px', width: '400px', border: '1px solid #cbd5e0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', color: '#111111' }}>
                        <h3 style={{ color: '#0066cc', marginTop: 0 }}>✉️ Direct Message: {targetMessageStudent.name}</h3>
                        <p style={{ fontSize: '12px', color: '#4a5568', marginBottom: '15px' }}>
                            Send a direct notification straight to this student's portal inbox.
                        </p>
                        
                        <textarea 
                            rows={4}
                            value={directMessageText}
                            onChange={e => setDirectMessageText(e.target.value)}
                            placeholder="Type your message here..."
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '13px', marginBottom: '15px', background: '#fff', color: '#111' }}
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button 
                                onClick={() => {
                                    setShowDirectMessageModal(false);
                                    setTargetMessageStudent(null);
                                    setDirectMessageText('');
                                }} 
                                style={{ background: '#e2e8f0', color: '#111', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSendDirectMessage}
                                style={{ background: '#0066cc', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                            >
                                Send Message 🚀
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InstructorDash;